import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, FlatList,
  TextInput, Modal, Alert, ActivityIndicator, RefreshControl,
  Switch, Platform, Share, Keyboard, KeyboardAvoidingView, Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as DocumentPicker from 'expo-document-picker';

const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P  = '#0e4e96';
const P2 = '#1e7be2';
const Y  = '#fdb913';

const getH = async () => {
  const tok = (await AsyncStorage.getItem('auth_token')) || (await AsyncStorage.getItem('token')) || '';
  return { 'Content-Type':'application/json','x-api-key':API_KEY,'Authorization':`Bearer ${tok}` };
};
const getBizId = async () => {
  const b = await AsyncStorage.getItem('company_id');
  return parseInt(b)||0;
};
const fmtGBP = (v) => `£${Number(v||0).toLocaleString('en-GB',{minimumFractionDigits:0,maximumFractionDigits:0})}`;
const fmtCur = (v, cur='GBP') => {
  const syms = {GBP:'£',USD:'$',EUR:'€',AED:'د.إ',SAR:'﷼',QAR:'﷼'};
  return `${syms[cur]||cur}${Number(v||0).toLocaleString('en-GB',{minimumFractionDigits:0,maximumFractionDigits:0})}`;
};
const daysLeft = (d) => {
  if (!d) return null;
  const diff = Math.ceil((new Date(d)-new Date())/(1000*60*60*24));
  return diff;
};
const fmtDate = (d) => { if (!d) return '—'; const dt=new Date(d); return dt.toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}); };
const newQUO = () => { const n=new Date(); return `QUO-${n.getFullYear()}${String(n.getMonth()+1).padStart(2,'0')}${String(n.getDate()).padStart(2,'0')}-${String(Math.floor(Math.random()*9000)+1000)}`; };
const uid = () => `_${Math.random().toString(36).substr(2,9)}`;

const CATEGORIES = [
  {id:'all',label:'All',icon:'view-grid'},
  {id:'construction',label:'Construction',icon:'crane'},
  {id:'engineering',label:'Engineering',icon:'cog'},
  {id:'architecture',label:'Architecture',icon:'office-building'},
  {id:'electrical',label:'Electrical',icon:'lightning-bolt'},
  {id:'plumbing',label:'Plumbing',icon:'pipe'},
  {id:'landscaping',label:'Landscaping',icon:'tree'},
  {id:'renovation',label:'Renovation',icon:'hammer'},
  {id:'maintenance',label:'Maintenance',icon:'wrench'},
  {id:'consulting',label:'Consulting',icon:'briefcase'},
  {id:'it',label:'IT',icon:'laptop'},
  {id:'civil',label:'Civil',icon:'road'},
  {id:'hvac',label:'HVAC',icon:'air-conditioner'},
  {id:'other',label:'Other',icon:'dots-horizontal'},
];
const CURRENCIES = ['GBP','USD','EUR','AED','SAR','QAR'];
const PAY_TERMS = ['7 days net','14 days net','30 days net','45 days net','60 days net','50% advance, 50% on completion','30% advance, 70% on completion','Monthly invoicing','On completion'];
const BID_STATUS_COLORS = {draft:'#64748B',submitted:P,under_review:'#d97706',won:'#059669',lost:'#dc2626',withdrawn:'#94a3b8'};
const QUOT_STATUS_COLORS = {draft:'#64748B',sent:P2,viewed:'#f59e0b',accepted:'#10b981',rejected:'#ef4444',expired:'#94a3b8',invoiced:'#8b5cf6'};

const SAMPLE_TENDERS = [
  {id:'t1',title:'Civil Engineering Consultancy – A34 Road Upgrade',organisation:'Oxfordshire County Council',category:'civil',location:'Oxford, UK',budgetMin:120000,budgetMax:180000,currency:'GBP',deadline:'2025-06-30',duration:'8 months',description:'Full civil engineering consultancy for a major road upgrade scheme including survey, design, stakeholder management and contract supervision.',requirements:['RIBA/ICE accreditation','PII min £5m','5+ years road schemes','ISO 9001'],bond:true,bondAmount:10000,tags:['urgent','public-sector'],views:214,applicants:7,status:'active'},
  {id:'t2',title:'Commercial HVAC Installation – Office Complex',organisation:'Savills Property Management',category:'hvac',location:'Manchester, UK',budgetMin:80000,budgetMax:130000,currency:'GBP',deadline:'2025-07-15',duration:'4 months',description:'Design, supply and installation of HVAC system for a 6-floor commercial office complex including BMS integration.',requirements:['F-Gas certification','REFCOM membership','Full design-and-build capability'],bond:false,tags:['commercial'],views:89,applicants:3,status:'active'},
  {id:'t3',title:'Electrical Upgrade – NHS Hospital',organisation:'NHS Property Services',category:'electrical',location:'Birmingham, UK',budgetMin:200000,budgetMax:350000,currency:'GBP',deadline:'2025-05-25',duration:'6 months',description:'LV switchgear replacement, power distribution upgrade and emergency lighting across main hospital building. HTM 06-01 compliance required.',requirements:['AM2 qualified electricians','NICEIC approval','Healthcare sector experience','CRB checked workforce'],bond:true,bondAmount:25000,tags:['urgent','public-sector','healthcare'],views:412,applicants:12,status:'active'},
  {id:'t4',title:'Landscape Design – New Housing Development',organisation:'Barratt Homes',category:'landscaping',location:'Bristol, UK',budgetMin:45000,budgetMax:75000,currency:'GBP',deadline:'2025-08-10',duration:'3 months',description:'Design and implementation of landscaping scheme for 120-unit residential development. Includes communal gardens, play areas and SUDS.',requirements:['Chartered Landscape Architect','BALI member','SUDS experience'],bond:false,tags:['residential'],views:67,applicants:2,status:'active'},
  {id:'t5',title:'IT Infrastructure Overhaul – Local Authority',organisation:'Leeds City Council',category:'it',location:'Leeds, UK',budgetMin:95000,budgetMax:140000,currency:'GBP',deadline:'2025-06-05',duration:'5 months',description:'Network refresh, server virtualisation, cloud migration and cybersecurity uplift across 12 council sites.',requirements:['Cyber Essentials Plus','PRINCE2 PM','Microsoft Gold Partner','SC cleared staff'],bond:false,tags:['public-sector','urgent'],views:178,applicants:5,status:'active'},
  {id:'t6',title:'Building Renovation – Grade II Listed Office',organisation:'Historic England',category:'renovation',location:'London, UK',budgetMin:250000,budgetMax:400000,currency:'GBP',deadline:'2025-09-01',duration:'12 months',description:'Full internal and external renovation of a Grade II listed Victorian office building. Heritage compliance and Listed Building Consent mandatory.',requirements:['Heritage contractor experience','SPAB membership','Conservation architect','LBC compliance'],bond:true,bondAmount:30000,tags:['heritage','public-sector'],views:95,applicants:4,status:'active'},
];

const SAMPLE_BIDS = [
  {id:'b1',tenderId:'t3',tenderTitle:'Electrical Upgrade – NHS Hospital',organisation:'NHS Property Services',status:'under_review',amount:285000,currency:'GBP',timeline:'6 months',validityDays:60,submittedDate:'2025-04-10',proposal:'We propose a phased LV switchgear replacement and full power distribution upgrade...',paymentTerms:'30% advance, 70% on completion',bondAmount:25000,deliverables:['Detailed design drawings','HTM 06-01 compliance report','Testing & commissioning certificates','O&M manuals'],team:[{name:'James Mitchell',role:'Project Manager',experience:'15 years'}],certifications:['NICEIC Approved','ISO 9001','ISO 14001'],attachments:[]},
  {id:'b2',tenderId:'t1',tenderTitle:'Civil Engineering Consultancy – A34',organisation:'Oxfordshire County Council',status:'draft',amount:155000,currency:'GBP',timeline:'8 months',validityDays:90,submittedDate:null,proposal:'Our multi-disciplinary team offers comprehensive civil engineering consultancy...',paymentTerms:'Monthly invoicing',bondAmount:10000,deliverables:['Feasibility study','Detailed design','Construction supervision'],team:[{name:'Sarah Chen',role:'Lead Engineer',experience:'12 years'}],certifications:['ICE Member','PII £5m+'],attachments:[]},
];

const SAMPLE_QUOTATIONS = [
  {id:'q1',quotationNumber:'QUO-20250401-5521',clientName:'Whitfield Construction Ltd',clientCompany:'Whitfield Construction',clientEmail:'procurement@whitfield.co.uk',clientPhone:'+44 20 7946 0001',clientAddress:'14 Park Lane, London W1K 1AA',title:'Structural Engineering Consultancy – Phase 2',description:'Structural design and site supervision for Phase 2 residential block.',status:'sent',currency:'GBP',createdDate:'2025-04-01',validUntil:'2025-05-01',items:[{id:'i1',description:'Senior Structural Engineer',quantity:15,unit:'day',unitPrice:375,total:5625},{id:'i2',description:'Structural Calculations Report',quantity:1,unit:'each',unitPrice:1500,total:1500},{id:'i3',description:'Site Visits (4 visits)',quantity:4,unit:'visit',unitPrice:350,total:1400}],vatRate:20,discountType:'percentage',discountValue:5,paymentTerms:'30 days net',notes:'Travel outside M25 charged at cost.',termsConditions:'Payment due within 30 days. IP retained until paid in full.'},
  {id:'q2',quotationNumber:'QUO-20250320-3312',clientName:'TechSpace Manchester',clientCompany:'TechSpace Ltd',clientEmail:'ops@techspace.co.uk',clientPhone:'+44 161 234 5678',clientAddress:'1 Piccadilly, Manchester M1 1RG',title:'Annual Maintenance & Support Contract',description:'12-month planned maintenance and emergency callout support.',status:'accepted',currency:'GBP',createdDate:'2025-03-20',validUntil:'2025-04-20',items:[{id:'i1',description:'Quarterly Planned Maintenance Visit',quantity:4,unit:'visit',unitPrice:650,total:2600},{id:'i2',description:'Annual Service Report',quantity:1,unit:'each',unitPrice:800,total:800},{id:'i3',description:'24/7 Emergency Callout (12 months)',quantity:1,unit:'package',unitPrice:2400,total:2400}],vatRate:20,discountType:'fixed',discountValue:200,paymentTerms:'Monthly invoicing',notes:'Parts charged at cost plus 15%.'},
];

const QUOT_TEMPLATES = [
  {id:'qt1',name:'Engineering Consultancy',items:[{description:'Senior Engineering Consultant',quantity:10,unit:'day',unitPrice:375,total:3750},{description:'Technical Report',quantity:1,unit:'each',unitPrice:1200,total:1200}],paymentTerms:'30 days net',notes:'Travel outside M25 charged at cost.'},
  {id:'qt2',name:'Construction Project',items:[{description:'Project Management',quantity:1,unit:'month',unitPrice:3000,total:3000},{description:'Site Assessment',quantity:1,unit:'each',unitPrice:1500,total:1500}],paymentTerms:'50% advance, 50% on completion',notes:'Subject to formal site survey.'},
  {id:'qt3',name:'Annual Maintenance',items:[{description:'Monthly Maintenance Visit',quantity:12,unit:'visit',unitPrice:450,total:5400},{description:'Parts & Materials',quantity:1,unit:'allowance',unitPrice:1000,total:1000}],paymentTerms:'Monthly invoicing',notes:'24/7 emergency callout available.'},
  {id:'qt4',name:'IT & Digital Services',items:[{description:'IT Consultant',quantity:20,unit:'day',unitPrice:500,total:10000},{description:'Infrastructure Audit',quantity:1,unit:'each',unitPrice:2000,total:2000}],paymentTerms:'30 days net',notes:'Remote working included.'},
  {id:'qt5',name:'Consulting Retainer',items:[{description:'Monthly Retainer',quantity:3,unit:'month',unitPrice:2500,total:7500},{description:'Ad-hoc Advice (est.)',quantity:5,unit:'hrs',unitPrice:200,total:1000}],paymentTerms:'Monthly invoicing in advance',notes:'Unused hours carried over for 1 month.'},
];

const TC_TEMPLATES = [
  {id:'tc1',name:'Standard Consultancy',text:'Payment due within 30 days of invoice. Late payment subject to 2% monthly interest. All work guaranteed for 12 months from completion. IP remains with consultant until full payment received.'},
  {id:'tc2',name:'Construction Contract',text:'Works subject to formal JCT contract. Payment milestones per project schedule. Retention of 5% held for 12 months after practical completion. Liquidated damages apply for delays caused by contractor.'},
  {id:'tc3',name:'Maintenance Services',text:'Monthly invoicing in advance. 30 days notice required to cancel. Emergency callout charged at 1.5x standard rate. Parts charged at cost plus 15%.'},
];

// ─── ACTIVITY LOG ──────────────────────────────────────────────────────────────
const logActivity = async (type, message, ref='') => {
  try {
    const raw = await AsyncStorage.getItem('tbq_activity');
    const log = raw ? JSON.parse(raw) : [];
    log.unshift({ id:uid(), type, message, ref, date:new Date().toISOString() });
    await AsyncStorage.setItem('tbq_activity', JSON.stringify(log.slice(0,50)));
  } catch {}
};

// ─── SECTION HEADER ────────────────────────────────────────────────────────────
const SectionHdr = ({ title, count, onAdd, addLabel='Add' }) => (
  <View style={ss.secHdr}>
    <Text style={ss.secHdrTxt}>{title}{count!=null ? ` (${count})` : ''}</Text>
    {onAdd && <TouchableOpacity onPress={onAdd} style={ss.secHdrBtn}><MaterialCommunityIcons name="plus" size={16} color="#fff"/><Text style={ss.secHdrBtnTxt}>{addLabel}</Text></TouchableOpacity>}
  </View>
);

// ─── COST SHEET CONSTANTS ─────────────────────────────────────────────────────
const DIRECT_CATEGORIES = [
  { id:'labour',        label:'Labour',               icon:'account-hard-hat', color:'#3b82f6' },
  { id:'materials',     label:'Materials',             icon:'package-variant',  color:'#10b981' },
  { id:'plant',         label:'Plant & Equipment',    icon:'excavator',        color:'#f59e0b' },
  { id:'subcontractors',label:'Subcontractors',        icon:'account-group',    color:'#8b5cf6' },
  { id:'site_costs',    label:'Site Costs',            icon:'home-city',        color:'#06b6d4' },
  { id:'transport',     label:'Transport',             icon:'truck',            color:'#ec4899' },
  { id:'other_direct',  label:'Other',                 icon:'dots-horizontal',  color:'#64748b' },
];
const INDIRECT_CATEGORIES = [
  { id:'prelims',        label:'Preliminaries',        icon:'clipboard-list',   color:'#0e4e96' },
  { id:'overhead',       label:'Head Office Overhead', icon:'office-building',  color:'#1e7be2' },
  { id:'insurance',      label:'Insurance & Bonds',    icon:'shield-check',     color:'#7c3aed' },
  { id:'contingency',    label:'Contingency',          icon:'lifebuoy',         color:'#dc2626' },
  { id:'risk',           label:'Risk Allowance',       icon:'alert-circle',     color:'#ea580c' },
  { id:'other_indirect', label:'Other',                icon:'dots-horizontal',  color:'#64748b' },
];
const RISK_LEVELS = [
  { id:'low',    label:'Low (1-3%)',    pct:2 },
  { id:'medium', label:'Medium (4-7%)', pct:5 },
  { id:'high',   label:'High (8-15%)', pct:10 },
  { id:'custom', label:'Custom',        pct:0 },
];
const defaultCostItem = () => ({ id:uid(), description:'', quantity:'1', unit:'nr', unitRate:'0', category:'', total:0 });

// ─── COST SHEET CALCULATOR ─────────────────────────────────────────────────────
const CostSheetCalculator = ({ value, onChange, currency='GBP' }) => {
  const [directItems,    setDirectItems]    = React.useState(value?.directItems   || []);
  const [indirectItems,  setIndirectItems]  = React.useState(value?.indirectItems || []);
  const [marginPct,      setMarginPct]      = React.useState(value?.marginPct     ?? 15);
  const [contingencyPct, setContingencyPct] = React.useState(value?.contingencyPct?? 5);
  const [riskLevel,      setRiskLevel]      = React.useState(value?.riskLevel     || 'medium');
  const [activeTab,      setActiveTab]      = React.useState('direct');
  const [expandedId,     setExpandedId]     = React.useState(null);

  const calcLine = item => (Number(item.quantity)||1)*(Number(item.unitRate)||0);
  const sumItems = items => items.reduce((a,i)=>a+calcLine(i),0);

  const directTotal   = sumItems(directItems);
  const indirectTotal = sumItems(indirectItems);
  const contAmt       = directTotal * (contingencyPct/100);
  const riskPct       = RISK_LEVELS.find(r=>r.id===riskLevel)?.pct || 0;
  const riskAmt       = directTotal * (riskPct/100);
  const subtotal      = directTotal + indirectTotal + contAmt + riskAmt;
  const marginAmt     = subtotal * (marginPct/100);
  const bidTotal      = subtotal + marginAmt;

  React.useEffect(()=>{
    onChange && onChange({ directItems, indirectItems, marginPct, contingencyPct, riskLevel,
      directTotal, indirectTotal, subtotal, marginAmt, bidTotal });
  },[directItems, indirectItems, marginPct, contingencyPct, riskLevel]);

  const addItem = isDir => {
    const item = defaultCostItem();
    if (isDir) setDirectItems(p=>[...p,item]);
    else setIndirectItems(p=>[...p,item]);
    setExpandedId(item.id);
  };

  const updItem = (isDir,id,field,val) => {
    const fn = items => items.map(i => i.id!==id ? i : { ...i, [field]:val });
    if (isDir) setDirectItems(fn); else setIndirectItems(fn);
  };

  const delItem = (isDir,id) => {
    if (isDir) setDirectItems(p=>p.filter(i=>i.id!==id));
    else setIndirectItems(p=>p.filter(i=>i.id!==id));
    if (expandedId===id) setExpandedId(null);
  };

  const renderCostItem = (item, isDir) => {
    const cats = isDir ? DIRECT_CATEGORIES : INDIRECT_CATEGORIES;
    const cat  = cats.find(c=>c.id===item.category) || cats[0];
    const lineTotal = calcLine(item);
    const expanded  = expandedId===item.id;
    return (
      <View key={item.id} style={{backgroundColor:'#fff',borderRadius:10,marginBottom:6,borderWidth:1,borderColor:'#e2e8f0',overflow:'hidden'}}>
        <TouchableOpacity style={{flexDirection:'row',alignItems:'center',padding:12,gap:10}}
          onPress={()=>setExpandedId(expanded?null:item.id)}>
          <View style={{width:32,height:32,borderRadius:8,backgroundColor:cat.color+'18',justifyContent:'center',alignItems:'center'}}>
            <MaterialCommunityIcons name={cat.icon} size={16} color={cat.color}/>
          </View>
          <View style={{flex:1}}>
            <Text style={{fontSize:13,fontWeight:'600',color:'#1e293b'}} numberOfLines={1}>{item.description||'New item'}</Text>
            <Text style={{fontSize:11,color:'#64748b',marginTop:1}}>{item.quantity||1} {item.unit||'nr'} x {fmtGBP(Number(item.unitRate)||0)}</Text>
          </View>
          <View style={{alignItems:'flex-end',gap:2}}>
            <Text style={{fontSize:14,fontWeight:'700',color:P}}>{fmtCur(lineTotal,currency)}</Text>
            <MaterialCommunityIcons name={expanded?'chevron-up':'chevron-down'} size={15} color="#94a3b8"/>
          </View>
        </TouchableOpacity>
        {expanded && (
          <View style={{padding:12,backgroundColor:'#f8fafc',borderTopWidth:1,borderTopColor:'#f0f4f8',gap:8}}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={{flexDirection:'row',gap:6}}>
                {cats.map(c=>(
                  <TouchableOpacity key={c.id}
                    style={{flexDirection:'row',alignItems:'center',gap:4,paddingHorizontal:9,paddingVertical:4,borderRadius:14,
                      backgroundColor:item.category===c.id?c.color+'20':'#f0f4f8',
                      borderWidth:1,borderColor:item.category===c.id?c.color:'transparent'}}
                    onPress={()=>updItem(isDir,item.id,'category',c.id)}>
                    <MaterialCommunityIcons name={c.icon} size={11} color={item.category===c.id?c.color:'#94a3b8'}/>
                    <Text style={{fontSize:10,fontWeight:'600',color:item.category===c.id?c.color:'#64748b'}}>{c.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
            <TextInput style={[ss.inp,{marginBottom:0}]}
              value={item.description} placeholderTextColor="#94a3b8" placeholder="Description"
              onChangeText={v=>updItem(isDir,item.id,'description',v)}/>
            <View style={{flexDirection:'row',gap:8}}>
              <View style={{flex:1}}>
                <Text style={{fontSize:10,fontWeight:'700',color:'#64748b',marginBottom:3}}>QTY</Text>
                <TextInput style={[ss.inp,{marginBottom:0,textAlign:'center'}]} keyboardType="numeric"
                  value={String(item.quantity||'')} placeholder="1" placeholderTextColor="#94a3b8"
                  onChangeText={v=>updItem(isDir,item.id,'quantity',v)}/>
              </View>
              <View style={{flex:1}}>
                <Text style={{fontSize:10,fontWeight:'700',color:'#64748b',marginBottom:3}}>UNIT</Text>
                <TextInput style={[ss.inp,{marginBottom:0,textAlign:'center'}]}
                  value={item.unit||''} placeholder="nr" placeholderTextColor="#94a3b8"
                  onChangeText={v=>updItem(isDir,item.id,'unit',v)}/>
              </View>
              <View style={{flex:1.5}}>
                <Text style={{fontSize:10,fontWeight:'700',color:'#64748b',marginBottom:3}}>UNIT RATE (£)</Text>
                <TextInput style={[ss.inp,{marginBottom:0,textAlign:'right'}]} keyboardType="numeric"
                  value={String(item.unitRate||'')} placeholder="0.00" placeholderTextColor="#94a3b8"
                  onChangeText={v=>updItem(isDir,item.id,'unitRate',v)}/>
              </View>
            </View>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingTop:4}}>
              <Text style={{fontSize:13,fontWeight:'700',color:P}}>Line total: {fmtCur(lineTotal,currency)}</Text>
              <TouchableOpacity onPress={()=>delItem(isDir,item.id)}
                style={{flexDirection:'row',alignItems:'center',gap:4,backgroundColor:'#fee2e2',paddingHorizontal:10,paddingVertical:5,borderRadius:8}}>
                <MaterialCommunityIcons name="trash-can-outline" size={13} color="#dc2626"/>
                <Text style={{fontSize:11,color:'#dc2626',fontWeight:'600'}}>Remove</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>
    );
  };

  const SRow = ({label,val,accent,bold,border}) => (
    <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',
      paddingVertical:7,borderTopWidth:border?1.5:0,borderTopColor:P,marginTop:border?6:0}}>
      <Text style={{fontSize:13,color:accent||'#64748b',fontWeight:bold?'700':'400'}}>{label}</Text>
      <Text style={{fontSize:bold?14:13,color:accent||'#1e293b',fontWeight:bold?'700':'600'}}>{val}</Text>
    </View>
  );

  return (
    <View>
      {/* Tab bar */}
      <View style={{flexDirection:'row',backgroundColor:'#f0f4f8',borderRadius:12,padding:4,marginBottom:14,gap:4}}>
        {[
          {id:'direct',   label:'Direct',   icon:'hammer',          badge:directItems.length},
          {id:'indirect', label:'Indirect', icon:'office-building', badge:indirectItems.length},
          {id:'summary',  label:'Summary',  icon:'calculator',      badge:null},
        ].map(t=>(
          <TouchableOpacity key={t.id} style={{flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',gap:5,
            paddingVertical:9,borderRadius:9,backgroundColor:activeTab===t.id?P:'transparent'}}
            onPress={()=>setActiveTab(t.id)}>
            <MaterialCommunityIcons name={t.icon} size={13} color={activeTab===t.id?'#fff':'#64748b'}/>
            <Text style={{fontSize:12,fontWeight:'700',color:activeTab===t.id?'#fff':'#64748b'}}>{t.label}</Text>
            {t.badge>0&&<View style={{backgroundColor:activeTab===t.id?'rgba(255,255,255,0.3)':'#e2e8f0',borderRadius:8,minWidth:16,height:16,alignItems:'center',justifyContent:'center',paddingHorizontal:4}}>
              <Text style={{fontSize:9,fontWeight:'700',color:activeTab===t.id?'#fff':'#64748b'}}>{t.badge}</Text>
            </View>}
          </TouchableOpacity>
        ))}
      </View>

      {/* Direct costs */}
      {activeTab==='direct' && (
        <View>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <Text style={{fontSize:12,color:'#64748b'}}>{directItems.length} items</Text>
            <TouchableOpacity onPress={()=>addItem(true)}
              style={{flexDirection:'row',alignItems:'center',gap:5,backgroundColor:P,borderRadius:8,paddingHorizontal:12,paddingVertical:7}}>
              <MaterialCommunityIcons name="plus" size={14} color="#fff"/>
              <Text style={{color:'#fff',fontSize:12,fontWeight:'700'}}>Add Item</Text>
            </TouchableOpacity>
          </View>
          {directItems.length===0 ? (
            <View style={{alignItems:'center',padding:28,backgroundColor:'#f8fafc',borderRadius:12,borderWidth:1.5,borderColor:'#e2e8f0',borderStyle:'dashed'}}>
              <MaterialCommunityIcons name="hammer" size={32} color="#cbd5e1"/>
              <Text style={{color:'#94a3b8',marginTop:8,fontSize:13,fontWeight:'600'}}>No direct costs yet</Text>
              <Text style={{color:'#cbd5e1',fontSize:12,marginTop:3}}>Labour, materials, plant, subcontractors…</Text>
            </View>
          ) : directItems.map(i=>renderCostItem(i,true))}
          {directItems.length>0 && (
            <View style={{backgroundColor:'#eff6ff',borderRadius:10,padding:12,marginTop:8,flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
              <Text style={{fontSize:13,color:P,fontWeight:'600'}}>Direct Total</Text>
              <Text style={{fontSize:16,fontWeight:'800',color:P}}>{fmtCur(directTotal,currency)}</Text>
            </View>
          )}
        </View>
      )}

      {/* Indirect costs */}
      {activeTab==='indirect' && (
        <View>
          <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <Text style={{fontSize:12,color:'#64748b'}}>{indirectItems.length} items</Text>
            <TouchableOpacity onPress={()=>addItem(false)}
              style={{flexDirection:'row',alignItems:'center',gap:5,backgroundColor:'#1e7be2',borderRadius:8,paddingHorizontal:12,paddingVertical:7}}>
              <MaterialCommunityIcons name="plus" size={14} color="#fff"/>
              <Text style={{color:'#fff',fontSize:12,fontWeight:'700'}}>Add Item</Text>
            </TouchableOpacity>
          </View>
          {indirectItems.length===0 ? (
            <View style={{alignItems:'center',padding:28,backgroundColor:'#f8fafc',borderRadius:12,borderWidth:1.5,borderColor:'#e2e8f0',borderStyle:'dashed'}}>
              <MaterialCommunityIcons name="office-building" size={32} color="#cbd5e1"/>
              <Text style={{color:'#94a3b8',marginTop:8,fontSize:13,fontWeight:'600'}}>No indirect costs yet</Text>
              <Text style={{color:'#cbd5e1',fontSize:12,marginTop:3}}>Overhead, prelims, insurance, admin…</Text>
            </View>
          ) : indirectItems.map(i=>renderCostItem(i,false))}
          <View style={{backgroundColor:'#fff7ed',borderRadius:10,padding:14,marginTop:8,borderWidth:1,borderColor:'#fed7aa'}}>
            <Text style={{fontSize:11,fontWeight:'700',color:'#ea580c',textTransform:'uppercase',letterSpacing:0.8,marginBottom:10}}>Auto Allowances (on Direct Costs)</Text>
            <View style={{flexDirection:'row',alignItems:'center',gap:8,marginBottom:10}}>
              <Text style={{flex:1,fontSize:13,color:'#475569'}}>Contingency</Text>
              <TextInput style={[ss.inp,{width:52,textAlign:'center',marginBottom:0,paddingVertical:7}]}
                value={String(contingencyPct)} keyboardType="numeric"
                onChangeText={v=>setContingencyPct(Math.max(0,Number(v)||0))}/>
              <Text style={{fontSize:12,color:'#64748b'}}>%</Text>
              <Text style={{fontSize:13,fontWeight:'700',color:'#ea580c',minWidth:60,textAlign:'right'}}>{fmtCur(contAmt,currency)}</Text>
            </View>
            <Text style={{fontSize:12,fontWeight:'600',color:'#64748b',marginBottom:6}}>Risk Level</Text>
            <View style={{flexDirection:'row',gap:6,marginBottom:8,flexWrap:'wrap'}}>
              {RISK_LEVELS.map(r=>(
                <TouchableOpacity key={r.id}
                  style={{paddingHorizontal:11,paddingVertical:5,borderRadius:14,
                    backgroundColor:riskLevel===r.id?'#ea580c20':'#f0f4f8',
                    borderWidth:1.5,borderColor:riskLevel===r.id?'#ea580c':'transparent'}}
                  onPress={()=>setRiskLevel(r.id)}>
                  <Text style={{fontSize:11,fontWeight:'600',color:riskLevel===r.id?'#ea580c':'#64748b'}}>{r.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
            <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
              <Text style={{fontSize:13,color:'#475569'}}>Risk Allowance ({riskPct}%)</Text>
              <Text style={{fontSize:13,fontWeight:'700',color:'#ea580c'}}>{fmtCur(riskAmt,currency)}</Text>
            </View>
          </View>
          {indirectItems.length>0&&(
            <View style={{backgroundColor:'#eff6ff',borderRadius:10,padding:12,marginTop:8,flexDirection:'row',justifyContent:'space-between',alignItems:'center'}}>
              <Text style={{fontSize:13,color:'#1e7be2',fontWeight:'600'}}>Indirect Total (incl. allowances)</Text>
              <Text style={{fontSize:16,fontWeight:'800',color:'#1e7be2'}}>{fmtCur(indirectTotal+contAmt+riskAmt,currency)}</Text>
            </View>
          )}
        </View>
      )}

      {/* Summary */}
      {activeTab==='summary' && (
        <View>
          <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,borderWidth:1,borderColor:'#e2e8f0',marginBottom:12}}>
            <Text style={{fontSize:11,fontWeight:'700',color:P,textTransform:'uppercase',letterSpacing:1,marginBottom:10}}>Cost Build-Up</Text>
            <SRow label="Direct Costs"                          val={fmtCur(directTotal,currency)}                  accent="#3b82f6" bold/>
            <SRow label="Indirect Costs"                        val={fmtCur(indirectTotal,currency)}                accent="#1e7be2" bold/>
            <SRow label={`Contingency (${contingencyPct}%)`}   val={fmtCur(contAmt,currency)}                     accent="#ea580c"/>
            <SRow label={`Risk Allowance (${riskPct}%)`}       val={fmtCur(riskAmt,currency)}                     accent="#dc2626"/>
            <SRow label="Cost Subtotal"                         val={fmtCur(subtotal,currency)}                    bold border/>
          </View>
          <View style={{backgroundColor:'#fff',borderRadius:14,padding:16,borderWidth:1,borderColor:'#e2e8f0',marginBottom:12}}>
            <Text style={{fontSize:11,fontWeight:'700',color:P,textTransform:'uppercase',letterSpacing:1,marginBottom:10}}>Profit Margin</Text>
            <View style={{flexDirection:'row',gap:6,marginBottom:12,flexWrap:'wrap'}}>
              {[5,10,15,20,25,30].map(p=>(
                <TouchableOpacity key={p}
                  style={{paddingHorizontal:14,paddingVertical:7,borderRadius:10,
                    backgroundColor:marginPct===p?P:'#f0f4f8',
                    borderWidth:1,borderColor:marginPct===p?P:'transparent'}}
                  onPress={()=>setMarginPct(p)}>
                  <Text style={{fontSize:13,fontWeight:'700',color:marginPct===p?'#fff':'#475569'}}>{p}%</Text>
                </TouchableOpacity>
              ))}
            </View>
            <View style={{flexDirection:'row',alignItems:'center',gap:10}}>
              <Text style={{flex:1,fontSize:13,color:'#64748b'}}>Custom margin %</Text>
              <TextInput style={[ss.inp,{width:70,textAlign:'center',marginBottom:0}]}
                value={String(marginPct)} keyboardType="numeric"
                onChangeText={v=>setMarginPct(Math.max(0,Number(v)||0))}/>
            </View>
            <View style={{flexDirection:'row',justifyContent:'space-between',marginTop:10,backgroundColor:'#eff6ff',padding:12,borderRadius:10}}>
              <Text style={{fontSize:13,color:P,fontWeight:'600'}}>Margin Amount</Text>
              <Text style={{fontSize:14,fontWeight:'700',color:P}}>{fmtCur(marginAmt,currency)}</Text>
            </View>
          </View>
          <View style={{backgroundColor:P,borderRadius:14,padding:20}}>
            <Text style={{color:'rgba(255,255,255,0.75)',fontSize:10,fontWeight:'700',textTransform:'uppercase',letterSpacing:1,marginBottom:4}}>Total Bid Value</Text>
            <Text style={{color:'#fff',fontSize:30,fontWeight:'800'}}>{fmtCur(bidTotal,currency)}</Text>
            <View style={{flexDirection:'row',gap:20,marginTop:12}}>
              <View><Text style={{color:'rgba(255,255,255,0.65)',fontSize:9,textTransform:'uppercase'}}>Cost</Text><Text style={{color:'#fff',fontSize:13,fontWeight:'700'}}>{fmtCur(subtotal,currency)}</Text></View>
              <View><Text style={{color:'rgba(255,255,255,0.65)',fontSize:9,textTransform:'uppercase'}}>Margin</Text><Text style={{color:'#fdb913',fontSize:13,fontWeight:'700'}}>{fmtCur(marginAmt,currency)}</Text></View>
              <View><Text style={{color:'rgba(255,255,255,0.65)',fontSize:9,textTransform:'uppercase'}}>Margin %</Text><Text style={{color:'#fdb913',fontSize:13,fontWeight:'700'}}>{marginPct}%</Text></View>
              <View><Text style={{color:'rgba(255,255,255,0.65)',fontSize:9,textTransform:'uppercase'}}>Markup</Text><Text style={{color:'rgba(255,255,255,0.9)',fontSize:13,fontWeight:'700'}}>{subtotal>0?(bidTotal/subtotal*100-100).toFixed(1):0}%</Text></View>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};



// ─── STATUS PILL ───────────────────────────────────────────────────────────────
const Pill = ({ label, color }) => (
  <View style={[ss.pill,{backgroundColor:color+'22',borderColor:color}]}>
    <Text style={[ss.pillTxt,{color}]}>{label.replace(/_/g,' ').toUpperCase()}</Text>
  </View>
);

// ─── TENDER CARD ───────────────────────────────────────────────────────────────
const TenderCard = ({ item, onPress, onSave, saved }) => {
  const dl = daysLeft(item.deadline);
  const cat = CATEGORIES.find(c=>c.id===item.category)||CATEGORIES[CATEGORIES.length-1];
  return (
    <TouchableOpacity style={ss.card} onPress={()=>onPress(item)} activeOpacity={0.85}>
      <View style={ss.cardRow}>
        <View style={[ss.catIcon,{backgroundColor:P}]}><MaterialCommunityIcons name={cat.icon} size={18} color="#fff"/></View>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={ss.cardTitle} numberOfLines={2}>{item.title}</Text>
          <Text style={ss.cardSub}>{item.organisation}</Text>
        </View>
        <TouchableOpacity onPress={()=>onSave(item.id)} style={{padding:4}}>
          <MaterialCommunityIcons name={saved?'bookmark':'bookmark-outline'} size={22} color={saved?Y:P2}/>
        </TouchableOpacity>
      </View>
      <View style={[ss.cardRow,{marginTop:8,flexWrap:'wrap',gap:6}]}>
        {item.tags?.map(t=>(
          <View key={t} style={ss.tagChip}><Text style={ss.tagChipTxt}>{t.toUpperCase()}</Text></View>
        ))}
        {item.bond && <View style={[ss.tagChip,{backgroundColor:'#fef3c7',borderColor:'#d97706'}]}><Text style={[ss.tagChipTxt,{color:'#d97706'}]}>BOND</Text></View>}
      </View>
      <View style={[ss.cardRow,{marginTop:10}]}>
        <View style={ss.metaItem}><MaterialCommunityIcons name="currency-gbp" size={14} color={P}/><Text style={ss.metaTxt}>{fmtGBP(item.budgetMin)}–{fmtGBP(item.budgetMax)}</Text></View>
        <View style={ss.metaItem}><MaterialCommunityIcons name="map-marker" size={14} color={P}/><Text style={ss.metaTxt}>{item.location}</Text></View>
      </View>
      <View style={[ss.cardRow,{marginTop:6}]}>
        <View style={ss.metaItem}><MaterialCommunityIcons name="clock-outline" size={14} color={dl!=null&&dl<=7?'#dc2626':'#64748B'}/><Text style={[ss.metaTxt,dl!=null&&dl<=7&&{color:'#dc2626',fontWeight:'700'}]}>{dl!=null?(dl<=0?'CLOSED':`${dl}d left`):'No deadline'}</Text></View>
        <View style={ss.metaItem}><MaterialCommunityIcons name="eye" size={14} color="#64748B"/><Text style={ss.metaTxt}>{item.views||0} views</Text></View>
        <View style={ss.metaItem}><MaterialCommunityIcons name="account-group" size={14} color="#64748B"/><Text style={ss.metaTxt}>{item.applicants||0} bids</Text></View>
      </View>
    </TouchableOpacity>
  );
};

// ─── BID CARD ──────────────────────────────────────────────────────────────────
const BidCard = ({ item, onPress, onEdit, onClone, onPDF, onWithdraw, onDelete }) => {
  const col = BID_STATUS_COLORS[item.status]||'#64748B';
  const dl = item.validityDays ? daysLeft(new Date(new Date(item.submittedDate||Date.now()).getTime()+item.validityDays*86400000).toISOString()) : null;
  return (
    <TouchableOpacity style={ss.card} onPress={()=>onPress(item)} activeOpacity={0.85}>
      <View style={ss.cardRow}>
        <View style={[ss.catIcon,{backgroundColor:col}]}><MaterialCommunityIcons name={item.status==='won'?'trophy':item.status==='lost'?'close-circle':item.status==='draft'?'file-edit':'send'} size={18} color="#fff"/></View>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={ss.cardTitle} numberOfLines={2}>{item.tenderTitle}</Text>
          <Text style={ss.cardSub}>{item.organisation}</Text>
        </View>
        <Pill label={item.status} color={col}/>
      </View>
      <View style={[ss.cardRow,{marginTop:10,gap:16}]}>
        <View style={ss.metaItem}><MaterialCommunityIcons name="currency-gbp" size={14} color={P}/><Text style={[ss.metaTxt,{fontWeight:'700',color:P}]}>{fmtCur(item.amount,item.currency)}</Text></View>
        <View style={ss.metaItem}><MaterialCommunityIcons name="calendar" size={14} color="#64748B"/><Text style={ss.metaTxt}>{item.timeline}</Text></View>
        {dl!=null&&<View style={ss.metaItem}><MaterialCommunityIcons name="timer-outline" size={14} color={dl<=7?'#dc2626':'#64748B'}/><Text style={[ss.metaTxt,dl<=7&&{color:'#dc2626'}]}>{dl<=0?'Expired':`${dl}d valid`}</Text></View>}
      </View>
      {(item.deliverables?.length>0||item.team?.length>0)&&(
        <View style={[ss.cardRow,{marginTop:6,gap:12}]}>
          {item.deliverables?.length>0&&<View style={ss.metaItem}><MaterialCommunityIcons name="check-all" size={14} color="#64748B"/><Text style={ss.metaTxt}>{item.deliverables.length} deliverables</Text></View>}
          {item.team?.length>0&&<View style={ss.metaItem}><MaterialCommunityIcons name="account-group" size={14} color="#64748B"/><Text style={ss.metaTxt}>{item.team.length} team</Text></View>}
          {item.attachments?.length>0&&<View style={ss.metaItem}><MaterialCommunityIcons name="paperclip" size={14} color="#64748B"/><Text style={ss.metaTxt}>{item.attachments.length} files</Text></View>}
        </View>
      )}
      <View style={[ss.cardRow,{marginTop:10,gap:8}]}>
        <TouchableOpacity style={ss.acBtn} onPress={()=>onEdit(item)}><MaterialCommunityIcons name="pencil" size={14} color={P}/><Text style={[ss.acTxt,{color:P}]}>Edit</Text></TouchableOpacity>
        <TouchableOpacity style={ss.acBtn} onPress={()=>onClone(item)}><MaterialCommunityIcons name="content-copy" size={14} color="#64748B"/><Text style={ss.acTxt}>Clone</Text></TouchableOpacity>
        <TouchableOpacity style={ss.acBtn} onPress={()=>onPDF(item)}><MaterialCommunityIcons name="file-pdf-box" size={14} color="#dc2626"/><Text style={[ss.acTxt,{color:'#dc2626'}]}>PDF</Text></TouchableOpacity>
        {item.status==='submitted'&&<TouchableOpacity style={ss.acBtn} onPress={()=>onWithdraw(item)}><MaterialCommunityIcons name="undo" size={14} color="#d97706"/><Text style={[ss.acTxt,{color:'#d97706'}]}>Withdraw</Text></TouchableOpacity>}
        {item.status==='draft'&&<TouchableOpacity style={ss.acBtn} onPress={()=>onDelete(item.id)}><MaterialCommunityIcons name="delete" size={14} color="#dc2626"/><Text style={[ss.acTxt,{color:'#dc2626'}]}>Delete</Text></TouchableOpacity>}
      </View>
    </TouchableOpacity>
  );
};

// ─── QUOTATION CARD ────────────────────────────────────────────────────────────
const QuotCard = ({ item, onPress, onPDF, onDuplicate, onReminder, onAccept, onReject, onInvoice, onDelete }) => {
  const col = QUOT_STATUS_COLORS[item.status]||'#64748B';
  const dl = daysLeft(item.validUntil);
  const sub = item.items?.reduce((a,i)=>a+(i.total||0),0)||0;
  const disc = item.discountType==='percentage' ? sub*(item.discountValue||0)/100 : (item.discountValue||0);
  const tax  = (sub-disc)*(item.vatRate||20)/100;
  const total = sub-disc+tax;
  return (
    <TouchableOpacity style={ss.card} onPress={()=>onPress(item)} activeOpacity={0.85}>
      <View style={ss.cardRow}>
        <View style={[ss.catIcon,{backgroundColor:col}]}><MaterialCommunityIcons name="file-document-outline" size={18} color="#fff"/></View>
        <View style={{flex:1,marginLeft:10}}>
          <Text style={ss.cardTitle} numberOfLines={1}>{item.title}</Text>
          <Text style={ss.cardSub}>{item.clientName} · {item.quotationNumber}</Text>
        </View>
        <Pill label={item.status} color={col}/>
      </View>
      <View style={[ss.cardRow,{marginTop:10,gap:16}]}>
        <View style={ss.metaItem}><MaterialCommunityIcons name="currency-gbp" size={14} color={P}/><Text style={[ss.metaTxt,{fontWeight:'700',color:P}]}>{fmtCur(total,item.currency)}</Text></View>
        <View style={ss.metaItem}><MaterialCommunityIcons name="format-list-bulleted" size={14} color="#64748B"/><Text style={ss.metaTxt}>{item.items?.length||0} items</Text></View>
        {dl!=null&&<View style={ss.metaItem}><MaterialCommunityIcons name="timer-outline" size={14} color={dl<=7?'#dc2626':'#64748B'}/><Text style={[ss.metaTxt,dl<=7&&{color:'#dc2626'}]}>{dl<=0?'Expired':`${dl}d left`}</Text></View>}
      </View>
      <View style={[ss.cardRow,{marginTop:8,gap:8,flexWrap:'wrap'}]}>
        <TouchableOpacity style={ss.acBtn} onPress={()=>onPDF(item)}><MaterialCommunityIcons name="file-pdf-box" size={14} color="#dc2626"/><Text style={[ss.acTxt,{color:'#dc2626'}]}>PDF</Text></TouchableOpacity>
        <TouchableOpacity style={ss.acBtn} onPress={()=>onDuplicate(item)}><MaterialCommunityIcons name="content-copy" size={14} color="#64748B"/><Text style={ss.acTxt}>Duplicate</Text></TouchableOpacity>
        {item.status==='sent'&&<TouchableOpacity style={ss.acBtn} onPress={()=>onReminder(item)}><MaterialCommunityIcons name="bell-ring" size={14} color={P2}/><Text style={[ss.acTxt,{color:P2}]}>Remind</Text></TouchableOpacity>}
        {item.status==='sent'&&<TouchableOpacity style={ss.acBtn} onPress={()=>onAccept(item.id)}><MaterialCommunityIcons name="check" size={14} color="#059669"/><Text style={[ss.acTxt,{color:'#059669'}]}>Accept</Text></TouchableOpacity>}
        {item.status==='sent'&&<TouchableOpacity style={ss.acBtn} onPress={()=>onReject(item.id)}><MaterialCommunityIcons name="close" size={14} color="#dc2626"/><Text style={[ss.acTxt,{color:'#dc2626'}]}>Reject</Text></TouchableOpacity>}
        {item.status==='accepted'&&<TouchableOpacity style={ss.acBtn} onPress={()=>onInvoice(item.id)}><MaterialCommunityIcons name="receipt" size={14} color="#8b5cf6"/><Text style={[ss.acTxt,{color:'#8b5cf6'}]}>Invoice</Text></TouchableOpacity>}
        <TouchableOpacity style={ss.acBtn} onPress={()=>onDelete(item.id,item.status)}><MaterialCommunityIcons name="delete" size={14} color="#dc2626"/><Text style={[ss.acTxt,{color:'#dc2626'}]}>Delete</Text></TouchableOpacity>
      </View>
    </TouchableOpacity>
  );
};

// ─── MAIN SCREEN ───────────────────────────────────────────────────────────────
// ─── UK LIVE TENDERS TAB ─────────────────────────────────────────────────────
const UK_API = `${API_BASE}/api/uk-tenders`;

const UK_SOURCES = [
  { id:'all',              label:'All Sources',        icon:'web' },
  { id:'contracts_finder', label:'Contracts Finder',   icon:'file-search' },
  { id:'find_a_tender',    label:'Find a Tender (FTS)',icon:'magnify' },
];

const UK_CATEGORIES = [
  {id:'',              label:'All'},
  {id:'construction',  label:'Construction'},
  {id:'engineering',   label:'Engineering'},
  {id:'it',            label:'IT & Digital'},
  {id:'consultancy',   label:'Consultancy'},
  {id:'facilities',    label:'Facilities'},
  {id:'health',        label:'Health'},
  {id:'transport',     label:'Transport'},
  {id:'environment',   label:'Environment'},
  {id:'security',      label:'Security'},
  {id:'legal',         label:'Legal'},
];

const UKTendersTab = ({ bizId }) => {
  const [results,   setResults]   = React.useState([]);
  const [loading,   setLoading]   = React.useState(false);
  const [query,     setQuery]     = React.useState('');
  const [category,  setCategory]  = React.useState('');
  const [source,    setSource]    = React.useState('all');
  const [page,      setPage]      = React.useState(1);
  const [total,     setTotal]     = React.useState(0);
  const [saved,     setSaved]     = React.useState({});
  const [showSearch,setShowSearch]= React.useState(false);
  const [detail,    setDetail]    = React.useState(null);

  const load = async (reset=false) => {
    setLoading(true);
    try {
      const pg  = reset ? 1 : page;
      const h   = await getH();
      const params = new URLSearchParams({ q:query, category, source, limit:20, page:pg });
      const res = await fetch(`${UK_API}/search?${params}`, { headers:h });
      const d   = await res.json();
      if (d.success) {
        const items = d.data || [];
        setResults(reset ? items : prev=>[...prev,...items]);
        setTotal(d.total || items.length);
        setPage(reset ? 2 : pg+1);
      }
    } catch (e) { console.warn('[UK Tenders]', e.message); }
    setLoading(false);
  };

  const loadSaved = async () => {
    try {
      const cid = await bizId();
      const h   = await getH();
      const res = await fetch(`${UK_API}/saved/${cid}`, { headers:h });
      const d   = await res.json();
      if (d.success) {
        const map = {};
        (d.data||[]).forEach(t=>{ map[t.tender_id]=true; });
        setSaved(map);
      }
    } catch {}
  };

  const saveTender = async (tender) => {
    try {
      const cid = await bizId();
      const h   = await getH();
      if (saved[tender.id]) {
        await fetch(`${UK_API}/save/${cid}/${encodeURIComponent(tender.id)}`,{method:'DELETE',headers:h});
        setSaved(s=>({...s,[tender.id]:false}));
      } else {
        await fetch(`${UK_API}/save`,{method:'POST',headers:h,body:JSON.stringify({company_id:cid,tender})});
        setSaved(s=>({...s,[tender.id]:true}));
      }
    } catch {}
  };

  React.useEffect(()=>{ load(true); loadSaved(); }, []);
  React.useEffect(()=>{ load(true); }, [category, source]);

  const openUrl = (url) => {
    const { Linking } = require('react-native');
    Linking.openURL(url).catch(()=>Alert.alert('Cannot open','Could not open URL'));
  };

  const renderCard = ({ item: t }) => {
    const isSaved = saved[t.id];
    const dl = t.deadline ? daysLeft(t.deadline) : null;
    const dlColor = dl===null?'#94a3b8':dl<=7?'#dc2626':dl<=30?'#f59e0b':'#10b981';
    return (
      <TouchableOpacity style={[ss.card,{borderLeftWidth:3,borderLeftColor:t._source==='contracts_finder'?'#0e4e96':'#1e7be2'}]}
        onPress={()=>setDetail(t)} activeOpacity={0.85}>
        <View style={ss.cardRow}>
          <View style={{flex:1}}>
            <View style={{flexDirection:'row',alignItems:'center',gap:6,marginBottom:4}}>
              <View style={{backgroundColor:t._source==='contracts_finder'?'#e0f2fe':'#ede9fe',paddingHorizontal:7,paddingVertical:2,borderRadius:6}}>
                <Text style={{fontSize:9,fontWeight:'700',color:t._source==='contracts_finder'?P:'#7c3aed'}}>
                  <Text>{t._source==='contracts_finder'?'CONTRACTS FINDER':'FIND A TENDER'}</Text>
                </Text>
              </View>
              {dl!==null&&<View style={{backgroundColor:dlColor+'18',paddingHorizontal:7,paddingVertical:2,borderRadius:6}}>
                <Text style={{fontSize:9,fontWeight:'700',color:dlColor}}>{dl<=0?'EXPIRED':`${dl}d left`}</Text>
              </View>}
            </View>
            <Text style={ss.cardTitle} numberOfLines={2}>{t.title}</Text>
            <Text style={ss.cardSub} numberOfLines={1}>{t.organisation}</Text>
          </View>
          <TouchableOpacity onPress={()=>saveTender(t)} style={{padding:4}}>
            <MaterialCommunityIcons name={isSaved?'bookmark':'bookmark-outline'} size={22} color={isSaved?Y:P}/>
          </TouchableOpacity>
        </View>
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginTop:8}}>
          {t.location&&<View style={ss.metaItem}><MaterialCommunityIcons name="map-marker-outline" size={13} color="#64748b"/><Text style={ss.metaTxt}>{t.location}</Text></View>}
          {(t.budgetMin>0||t.budgetMax>0)&&<View style={ss.metaItem}><MaterialCommunityIcons name="currency-gbp" size={13} color={P}/><Text style={ss.metaTxt}>{t.budgetMin>0?fmtGBP(t.budgetMin):''}{t.budgetMax>t.budgetMin?`–${fmtGBP(t.budgetMax)}`:''}</Text></View>}
          {t.deadline&&<View style={ss.metaItem}><MaterialCommunityIcons name="calendar-clock" size={13} color="#64748b"/><Text style={ss.metaTxt}>{fmtDate(t.deadline)}</Text></View>}
        </View>
        <TouchableOpacity onPress={()=>openUrl(t.sourceUrl)}
          style={{flexDirection:'row',alignItems:'center',gap:4,marginTop:8,alignSelf:'flex-start'}}>
          <MaterialCommunityIcons name="open-in-new" size={13} color={P}/>
          <Text style={{fontSize:12,color:P,fontWeight:'600'}}>View on {t._source==='contracts_finder'?'Contracts Finder':'Find a Tender'}</Text>
        </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  return (
    <View style={{flex:1,backgroundColor:'#f8fafc'}}>
      {/* Header */}
      <View style={{backgroundColor:P,padding:14,paddingBottom:10}}>
        <View style={{flexDirection:'row',alignItems:'center',gap:10,marginBottom:10}}>
          <MaterialCommunityIcons name="web" size={20} color="#fff"/>
          <View style={{flex:1}}>
            <Text style={{color:'#fff',fontSize:15,fontWeight:'700'}}>UK Live Tenders</Text>
            <Text style={{color:'rgba(255,255,255,0.75)',fontSize:11}}>{total>0?`${total} live opportunities`:'Contracts Finder & Find a Tender'}</Text>
          </View>
          <TouchableOpacity onPress={()=>setShowSearch(s=>!s)}
            style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:20,padding:8}}>
            <MaterialCommunityIcons name={showSearch?'close':'magnify'} size={18} color="#fff"/>
          </TouchableOpacity>
          <TouchableOpacity onPress={()=>load(true)}
            style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:20,padding:8}}>
            <MaterialCommunityIcons name="refresh" size={18} color="#fff"/>
          </TouchableOpacity>
        </View>

        {showSearch&&(
          <View style={{flexDirection:'row',gap:8,marginBottom:8}}>
            <View style={{flex:1,backgroundColor:'rgba(255,255,255,0.15)',borderRadius:10,flexDirection:'row',alignItems:'center',paddingHorizontal:10}}>
              <MaterialCommunityIcons name="magnify" size={16} color="rgba(255,255,255,0.7)"/>
              <TextInput style={{flex:1,color:'#fff',fontSize:13,paddingVertical:9,paddingLeft:8}}
                value={query} onChangeText={setQuery} placeholder="Search tenders..." placeholderTextColor="rgba(255,255,255,0.5)"
                onSubmitEditing={()=>load(true)} returnKeyType="search"/>
            </View>
            <TouchableOpacity onPress={()=>load(true)}
              style={{backgroundColor:'#fdb913',borderRadius:10,paddingHorizontal:14,justifyContent:'center'}}>
              <Text style={{color:P,fontWeight:'700',fontSize:13}}>Go</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* Source filter */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={{flexDirection:'row',gap:6,paddingBottom:4}}>
            {UK_SOURCES.map(s=>(
              <TouchableOpacity key={s.id}
                style={{flexDirection:'row',alignItems:'center',gap:5,paddingHorizontal:12,paddingVertical:6,borderRadius:16,
                  backgroundColor:source===s.id?'#fff':'rgba(255,255,255,0.15)'}}
                onPress={()=>setSource(s.id)}>
                <MaterialCommunityIcons name={s.icon} size={12} color={source===s.id?P:'rgba(255,255,255,0.85)'}/>
                <Text style={{fontSize:11,fontWeight:'600',color:source===s.id?P:'rgba(255,255,255,0.85)'}}>{s.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Category chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#f0f4f8'}}>
        <View style={{flexDirection:'row',gap:6,padding:10}}>
          {UK_CATEGORIES.map(c=>(
            <TouchableOpacity key={c.id}
              style={{paddingHorizontal:14,paddingVertical:6,borderRadius:16,
                backgroundColor:category===c.id?P:'#f0f4f8',
                borderWidth:1,borderColor:category===c.id?P:'transparent'}}
              onPress={()=>setCategory(c.id)}>
              <Text style={{fontSize:12,fontWeight:'600',color:category===c.id?'#fff':'#64748b'}}>{c.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {/* Results */}
      {loading && results.length===0 ? (
        <View style={{flex:1,alignItems:'center',justifyContent:'center',gap:12}}>
          <ActivityIndicator size="large" color={P}/>
          <Text style={{color:'#64748b',fontSize:13}}>Fetching live UK tenders…</Text>
        </View>
      ) : results.length===0 ? (
        <View style={{flex:1,alignItems:'center',justifyContent:'center',padding:32}}>
          <MaterialCommunityIcons name="web-off" size={48} color="#cbd5e1"/>
          <Text style={{fontSize:16,fontWeight:'700',color:'#475569',marginTop:12}}>No results found</Text>
          <Text style={{fontSize:13,color:'#94a3b8',marginTop:6,textAlign:'center'}}>Try a different keyword or category</Text>
          <TouchableOpacity onPress={()=>load(true)} style={{marginTop:16,backgroundColor:P,borderRadius:10,paddingHorizontal:20,paddingVertical:10}}>
            <Text style={{color:'#fff',fontWeight:'700'}}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={results}
          keyExtractor={(t,i)=>t.id||String(i)}
          renderItem={renderCard}
          contentContainerStyle={{padding:14,gap:10,paddingBottom:100}}
          refreshControl={<RefreshControl refreshing={loading} onRefresh={()=>load(true)} tintColor={P}/>}
          onEndReached={()=>{ if(!loading) load(false); }}
          onEndReachedThreshold={0.4}
          ListFooterComponent={loading&&results.length>0?<ActivityIndicator color={P} style={{padding:16}}/>:null}
        />
      )}

      {/* Detail Modal */}
      {detail&&(
        <Modal visible animationType="slide" onRequestClose={()=>setDetail(null)}>
          <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
            <View style={ss.modalHdr}>
              <TouchableOpacity onPress={()=>setDetail(null)}><MaterialCommunityIcons name="arrow-left" size={24} color="#fff"/></TouchableOpacity>
              <Text style={ss.modalTitle} numberOfLines={1}>{detail.title}</Text>
              <TouchableOpacity onPress={()=>saveTender(detail)}>
                <MaterialCommunityIcons name={saved[detail.id]?'bookmark':'bookmark-outline'} size={24} color={saved[detail.id]?Y:'rgba(255,255,255,0.8)'}/>
              </TouchableOpacity>
            </View>
            <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>

              {/* Hero */}
              <View style={{backgroundColor:P,borderRadius:16,padding:20,marginBottom:14}}>
                <View style={{flexDirection:'row',alignItems:'center',gap:6,marginBottom:8,flexWrap:'wrap'}}>
                  <View style={{backgroundColor:'rgba(255,255,255,0.2)',paddingHorizontal:10,paddingVertical:3,borderRadius:8}}>
                    <Text style={{color:'#fff',fontSize:10,fontWeight:'700'}}>{detail._source==='contracts_finder'?'CONTRACTS FINDER':'FIND A TENDER'}</Text>
                  </View>
                  {detail.awarded&&<View style={{backgroundColor:'#fdb913',paddingHorizontal:10,paddingVertical:3,borderRadius:8}}><Text style={{color:P,fontSize:10,fontWeight:'700'}}>AWARDED</Text></View>}
                  {detail.framework&&<View style={{backgroundColor:'rgba(255,255,255,0.15)',paddingHorizontal:10,paddingVertical:3,borderRadius:8}}><Text style={{color:'#fff',fontSize:10,fontWeight:'700'}}>FRAMEWORK</Text></View>}
                  {detail.sme&&<View style={{backgroundColor:'#10b981',paddingHorizontal:10,paddingVertical:3,borderRadius:8}}><Text style={{color:'#fff',fontSize:10,fontWeight:'700'}}>SME FRIENDLY</Text></View>}
                </View>
                <Text style={{color:'#fff',fontSize:16,fontWeight:'700',marginBottom:4,lineHeight:22}}>{detail.title}</Text>
                <Text style={{color:'rgba(255,255,255,0.85)',fontSize:13,marginBottom:12}}>{detail.organisation}</Text>
                <View style={{flexDirection:'row',gap:10,flexWrap:'wrap'}}>
                  {detail.budgetFormatted&&detail.budgetFormatted!=='Not specified'&&(
                    <View style={{backgroundColor:'rgba(255,255,255,0.15)',borderRadius:10,padding:10,minWidth:130}}>
                      <Text style={{color:'rgba(255,255,255,0.7)',fontSize:9,textTransform:'uppercase',letterSpacing:0.8}}>Est. Value</Text>
                      <Text style={{color:'#fff',fontSize:15,fontWeight:'800',marginTop:2}}>{detail.budgetFormatted}</Text>
                    </View>
                  )}
                  {!detail.budgetFormatted&&(detail.budgetMin>0||detail.budgetMax>0)&&(
                    <View style={{backgroundColor:'rgba(255,255,255,0.15)',borderRadius:10,padding:10}}>
                      <Text style={{color:'rgba(255,255,255,0.7)',fontSize:9,textTransform:'uppercase'}}>Est. Value</Text>
                      <Text style={{color:'#fff',fontSize:15,fontWeight:'800',marginTop:2}}>{fmtGBP(detail.budgetMin)}{detail.budgetMax>detail.budgetMin?` – ${fmtGBP(detail.budgetMax)}`:''}</Text>
                    </View>
                  )}
                  {detail.deadline&&(
                    <View style={{backgroundColor:'rgba(255,255,255,0.15)',borderRadius:10,padding:10,minWidth:110}}>
                      <Text style={{color:'rgba(255,255,255,0.7)',fontSize:9,textTransform:'uppercase',letterSpacing:0.8}}>Deadline</Text>
                      <Text style={{color:'#fff',fontSize:13,fontWeight:'700',marginTop:2}}>{fmtDate(detail.deadline)}</Text>
                      {daysLeft(detail.deadline)!==null&&<Text style={{color:'#fdb913',fontSize:11,marginTop:1}}>{daysLeft(detail.deadline)<=0?'EXPIRED':`${daysLeft(detail.deadline)}d left`}</Text>}
                    </View>
                  )}
                  {detail.applicants>0&&(
                    <View style={{backgroundColor:'rgba(255,255,255,0.15)',borderRadius:10,padding:10}}>
                      <Text style={{color:'rgba(255,255,255,0.7)',fontSize:9,textTransform:'uppercase',letterSpacing:0.8}}>Tenderers</Text>
                      <Text style={{color:'#fff',fontSize:15,fontWeight:'800',marginTop:2}}>{detail.applicants}</Text>
                    </View>
                  )}
                </View>
              </View>

              {/* Info grid */}
              <View style={ss.grid2}>
                {detail.location&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Location</Text><Text style={ss.detailVal}>{detail.location}</Text></View>}
                {detail.publishDate&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Published</Text><Text style={ss.detailVal}>{fmtDate(detail.publishDate)}</Text></View>}
                {detail.procurementMethod&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Method</Text><Text style={ss.detailVal}>{detail.procurementMethod.replace(/_/g,' ')}</Text></View>}
                {detail.mainCategory&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Category</Text><Text style={ss.detailVal}>{detail.mainCategory}</Text></View>}
                {detail.contractStart&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Contract Start</Text><Text style={ss.detailVal}>{fmtDate(detail.contractStart)}</Text></View>}
                {detail.contractEnd&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Contract End</Text><Text style={ss.detailVal}>{fmtDate(detail.contractEnd)}</Text></View>}
                {detail.numberOfLots>0&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Lots</Text><Text style={ss.detailVal}>{detail.numberOfLots}</Text></View>}
                {detail.awardValue>0&&<View style={ss.detailBox}><Text style={ss.detailLbl}>Award Value</Text><Text style={ss.detailVal}>{fmtGBP(detail.awardValue)}</Text></View>}
              </View>

              {detail.description&&<View style={ss.descBox}><Text style={ss.descLbl}>Description</Text><Text style={ss.descTxt}>{detail.description}</Text></View>}
              {detail.procurementMethodDetails&&<View style={ss.descBox}><Text style={ss.descLbl}>Procurement Method</Text><Text style={ss.descTxt}>{detail.procurementMethodDetails}</Text></View>}
              {detail.eligibilityCriteria&&<View style={ss.descBox}><Text style={ss.descLbl}>Eligibility Criteria</Text><Text style={ss.descTxt}>{detail.eligibilityCriteria}</Text></View>}

              {detail.requirements?.length>0&&(
                <View style={ss.descBox}>
                  <Text style={ss.descLbl}>Requirements</Text>
                  {detail.requirements.map((r,i)=>(
                    <View key={i} style={{flexDirection:'row',gap:8,marginBottom:7,alignItems:'flex-start'}}>
                      <MaterialCommunityIcons name="check-circle-outline" size={16} color={P} style={{marginTop:1}}/>
                      <Text style={[ss.descTxt,{flex:1}]}>{r}</Text>
                    </View>
                  ))}
                </View>
              )}

              {detail.lots?.length>0&&(
                <View style={ss.descBox}>
                  <Text style={ss.descLbl}>Lots ({detail.lots.length})</Text>
                  {detail.lots.map((l,i)=>(
                    <View key={i} style={{backgroundColor:'#f8fafc',borderRadius:10,padding:12,marginBottom:8,borderWidth:1,borderColor:'#e2e8f0'}}>
                      <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'flex-start'}}>
                        <Text style={{fontSize:13,fontWeight:'700',color:P,flex:1}}>{l.title||`Lot ${i+1}`}</Text>
                        {l.value>0&&<Text style={{fontSize:13,fontWeight:'700',color:'#059669'}}>{fmtGBP(l.value)}</Text>}
                      </View>
                      {l.description&&<Text style={{fontSize:12,color:'#475569',marginTop:4,lineHeight:18}}>{l.description}</Text>}
                    </View>
                  ))}
                </View>
              )}

              {detail.awarded&&detail.awards?.length>0&&(
                <View style={{backgroundColor:'#f0fdf4',borderRadius:12,padding:14,marginBottom:12,borderWidth:1,borderColor:'#bbf7d0'}}>
                  <Text style={{fontSize:11,fontWeight:'700',color:'#059669',textTransform:'uppercase',letterSpacing:0.8,marginBottom:8}}>Award Information</Text>
                  {detail.awards.map((a,i)=>(
                    <View key={i}>
                      {a.supplier&&<View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}><Text style={{fontSize:12,color:'#475569'}}>Awarded to</Text><Text style={{fontSize:13,fontWeight:'700',color:'#059669',flex:1,textAlign:'right'}}>{a.supplier}</Text></View>}
                      {a.value>0&&<View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:4}}><Text style={{fontSize:12,color:'#475569'}}>Value</Text><Text style={{fontSize:13,fontWeight:'700',color:'#059669'}}>{fmtGBP(a.value)}</Text></View>}
                      {a.date&&<View style={{flexDirection:'row',justifyContent:'space-between'}}><Text style={{fontSize:12,color:'#475569'}}>Date</Text><Text style={{fontSize:13,fontWeight:'600',color:'#1e293b'}}>{fmtDate(a.date)}</Text></View>}
                    </View>
                  ))}
                </View>
              )}

              {(detail.contact?.name||detail.contact?.email||detail.contact?.phone)&&(
                <View style={ss.descBox}>
                  <Text style={ss.descLbl}>Contact</Text>
                  {detail.contact.name&&<Text style={{fontSize:14,fontWeight:'700',color:'#1e293b',marginBottom:6}}>{detail.contact.name}</Text>}
                  {detail.contact.email&&<TouchableOpacity style={{flexDirection:'row',alignItems:'center',gap:8,marginBottom:6}} onPress={()=>openUrl(`mailto:${detail.contact.email}`)}><MaterialCommunityIcons name="email-outline" size={15} color={P}/><Text style={{fontSize:13,color:P}}>{detail.contact.email}</Text></TouchableOpacity>}
                  {detail.contact.phone&&<TouchableOpacity style={{flexDirection:'row',alignItems:'center',gap:8,marginBottom:6}} onPress={()=>openUrl(`tel:${detail.contact.phone}`)}><MaterialCommunityIcons name="phone-outline" size={15} color={P}/><Text style={{fontSize:13,color:P}}>{detail.contact.phone}</Text></TouchableOpacity>}
                  {detail.fullAddress?.locality&&<View style={{flexDirection:'row',alignItems:'flex-start',gap:8,marginTop:2}}><MaterialCommunityIcons name="map-marker-outline" size={15} color="#64748b"/><Text style={{fontSize:12,color:'#64748b',flex:1}}>{[detail.fullAddress.street,detail.fullAddress.locality,detail.fullAddress.region,detail.fullAddress.postcode].filter(Boolean).join(', ')}</Text></View>}
                </View>
              )}

              {detail.documents?.length>0&&(
                <View style={ss.descBox}>
                  <Text style={ss.descLbl}>Documents ({detail.documents.length})</Text>
                  {detail.documents.map((d,i)=>(
                    <TouchableOpacity key={i} onPress={()=>d.url&&openUrl(d.url)}
                      style={{flexDirection:'row',alignItems:'center',gap:10,paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#f0f4f8'}}>
                      <MaterialCommunityIcons name={d.format?.includes('pdf')?'file-pdf-box':'file-document-outline'} size={22} color={P}/>
                      <View style={{flex:1}}>
                        <Text style={{fontSize:13,fontWeight:'600',color:'#1e293b'}} numberOfLines={1}>{d.title}</Text>
                        {d.documentType&&<Text style={{fontSize:11,color:'#64748b',marginTop:1}}>{d.documentType}</Text>}
                        {d.datePublished&&<Text style={{fontSize:11,color:'#94a3b8'}}>{fmtDate(d.datePublished)}</Text>}
                      </View>
                      {d.url&&<MaterialCommunityIcons name="open-in-new" size={16} color="#94a3b8"/>}
                    </TouchableOpacity>
                  ))}
                </View>
              )}

              {detail.cpvCodes?.length>0&&(
                <View style={ss.descBox}>
                  <Text style={ss.descLbl}>CPV Codes</Text>
                  {detail.cpvCodes.map((c,i)=>(
                    <View key={i} style={{flexDirection:'row',alignItems:'center',gap:10,paddingVertical:5,borderBottomWidth:1,borderBottomColor:'#f8fafc'}}>
                      <View style={{backgroundColor:'#eff6ff',borderRadius:6,paddingHorizontal:8,paddingVertical:3}}><Text style={{fontSize:11,fontWeight:'700',color:P}}>{c.code||c}</Text></View>
                      {c.desc&&<Text style={{fontSize:12,color:'#475569',flex:1}}>{c.desc}</Text>}
                    </View>
                  ))}
                </View>
              )}

              {detail.tags?.length>0&&(
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginBottom:12}}>
                  {detail.tags.map((t,i)=><View key={i} style={{backgroundColor:'#f0f4f8',borderRadius:16,paddingHorizontal:10,paddingVertical:4}}><Text style={{fontSize:11,color:'#64748b',fontWeight:'600'}}>#{String(t).replace(/-/g,' ')}</Text></View>)}
                </View>
              )}

            </ScrollView>
            <View style={ss.modalFooter}>
              <TouchableOpacity style={ss.footerBtnSec} onPress={()=>saveTender(detail)}>
                <MaterialCommunityIcons name={saved[detail.id]?'bookmark-minus':'bookmark-plus'} size={18} color={P}/>
                <Text style={[ss.footerBtnSecTxt,{color:P}]}>{saved[detail.id]?'Unsave':'Save'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={ss.footerBtnSec} onPress={()=>{setDetail(null);setTimeout(()=>{setEditingBid({tenderTitle:detail.title,organisation:detail.organisation,currency:'GBP'});setShowBidForm(true);setMainTab('bids');},300);}}>
                <MaterialCommunityIcons name="send-outline" size={18} color="#8b5cf6"/>
                <Text style={[ss.footerBtnSecTxt,{color:'#8b5cf6'}]}>Create Bid</Text>
              </TouchableOpacity>
              <TouchableOpacity style={ss.footerBtnPri} onPress={()=>openUrl(detail.sourceUrl)}>
                <MaterialCommunityIcons name="open-in-new" size={18} color="#fff"/>
                <Text style={ss.footerBtnPriTxt}>Apply Online</Text>
              </TouchableOpacity>
            </View>
          </SafeAreaView>
        </Modal>
      )}
    </View>
  );
};

export default function TenderBidQuotationScreen({ navigation, route }) {
  const initTab = route?.params?.tab || 'tenders';
  const [mainTab, setMainTab] = useState(initTab);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Tenders
  const [tenders, setTenders] = useState([]);
  const [myTenders, setMyTenders] = useState([]);
  const [savedTenders, setSavedTenders] = useState([]);
  const [tenderSearch, setTenderSearch] = useState('');
  const [tenderSubTab, setTenderSubTab] = useState('available');
  const [tenderSort, setTenderSort] = useState('deadline');
  const [tenderFilter, setTenderFilter] = useState({category:'all',budgetMin:'',budgetMax:'',location:'',bond:false});
  const [showTenderFilter, setShowTenderFilter] = useState(false);
  const [showTenderSort, setShowTenderSort] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [showTenderDetail, setShowTenderDetail] = useState(false);
  const [showMyTenderForm, setShowMyTenderForm] = useState(false);
  const [editingMyTender, setEditingMyTender] = useState(null);
  // Bids
  const [submittedBids, setSubmittedBids] = useState([]);
  const [draftBids, setDraftBids] = useState([]);
  const [bidSubTab, setBidSubTab] = useState('active');
  const [selectedBid, setSelectedBid] = useState(null);
  const [showBidDetail, setShowBidDetail] = useState(false);
  const [showBidForm, setShowBidForm] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  // Quotations
  const [sentQuotations, setSentQuotations] = useState([]);
  const [draftQuotations, setDraftQuotations] = useState([]);
  const [quotSubTab, setQuotSubTab] = useState('active');
  const [selectedQuot, setSelectedQuot] = useState(null);
  const [showQuotDetail, setShowQuotDetail] = useState(false);
  const [showQuotForm, setShowQuotForm] = useState(false);
  const [editingQuot, setEditingQuot] = useState(null);
  // Analytics
  const [activityLog, setActivityLog] = useState([]);
  // Company logo
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(()=>{
    loadAll();
    AsyncStorage.getItem('company_profile').then(raw=>{
      if(!raw) return;
      const p = JSON.parse(raw);
      const logo = p.logo||p.logo_url||p.company_logo||p.banner_image_url;
      if(logo) setCompanyLogo(logo.startsWith('http') || logo.startsWith('data:')?logo:`${API_BASE}${logo}`);
    }).catch(()=>{});
  },[]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadTenders(), loadBids(), loadQuotations(), loadActivity()]);
    setLoading(false);
  };
  const onRefresh = async () => { setRefreshing(true); await loadAll(); setRefreshing(false); };

  // ─── DB → local field normalizers ────────────────────────────────────────────
  const normTender = (t) => ({
    ...t,
    title:        t.title        || t.name || '',
    organisation: t.organisation || t.organization || t.client_name || '',
    budgetMin:    parseFloat(t.budgetMin  || t.budget_min  || 0),
    budgetMax:    parseFloat(t.budgetMax  || t.budget_max  || 0),
    bondAmount:   parseFloat(t.bondAmount || t.bond_amount || 0),
    deadline:     t.deadline     || t.deadline_date || null,
    requirements: Array.isArray(t.requirements) ? t.requirements : [],
    tags:         Array.isArray(t.tags)         ? t.tags         : [],
    documents:    Array.isArray(t.documents)    ? t.documents    : [],
    views:        parseInt(t.views     || 0),
    applicants:   parseInt(t.applicants|| 0),
  });

  const normBid = (b) => ({
    ...b,
    tenderId:     b.tenderId     || b.tender_id     || '',
    tenderTitle:  b.tenderTitle  || b.tender_title  || b.title || '',
    organisation: b.organisation || b.organization  || '',
    amount:       parseFloat(b.amount      || 0),
    bondAmount:   parseFloat(b.bondAmount  || b.bond_amount || 0),
    validityDays: parseInt(b.validityDays  || b.validity_days || 60),
    submittedDate:b.submittedDate|| b.submitted_date|| null,
    paymentTerms: b.paymentTerms || b.payment_terms || '',
    deliverables: Array.isArray(b.deliverables)    ? b.deliverables : [],
    team:         Array.isArray(b.team)             ? b.team         : [],
    certifications:Array.isArray(b.certifications) ? b.certifications:[],
    attachments:  Array.isArray(b.attachments)     ? b.attachments  : [],
  });

  const normQuot = (q) => {
    const items = Array.isArray(q.items) ? q.items : [];
    const sub   = items.reduce((a,i)=>a+(i.total||i.unitPrice*i.quantity||0),0);
    const disc  = q.discountType==='percentage'||q.discount_type==='percentage'
                  ? sub*((q.discountValue||q.discount_value||0)/100)
                  : parseFloat(q.discountValue||q.discount_value||0);
    const vat   = parseFloat(q.vatRate||q.vat_rate||20);
    return {
      ...q,
      quotationNumber: q.quotationNumber || q.quotation_number || q.reference || '',
      clientName:      q.clientName      || q.client_name      || q.client    || '',
      clientCompany:   q.clientCompany   || q.client_company   || '',
      clientEmail:     q.clientEmail     || q.client_email     || '',
      clientPhone:     q.clientPhone     || q.client_phone     || '',
      clientAddress:   q.clientAddress   || q.client_address   || '',
      title:           q.title           || q.name             || '',
      validUntil:      q.validUntil      || q.valid_until      || q.expiry_date || null,
      createdDate:     q.createdDate     || q.created_date     || q.created_at  || null,
      vatRate:         vat,
      discountType:    q.discountType    || q.discount_type    || 'percentage',
      discountValue:   parseFloat(q.discountValue || q.discount_value || 0),
      paymentTerms:    q.paymentTerms    || q.payment_terms    || '',
      items,
      subtotal:        sub,
      discount:        disc,
      tax:             (sub-disc)*(vat/100),
      total:           (sub-disc)*(1+vat/100),
    };
  };

  const loadTenders = async () => {
    try {
      const bizId = await getBizId();
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/company/data/tenders?company_id=${bizId}&limit=200`,{headers:h});
      if (res.ok) {
        const d = await res.json();
        const raw = d.tenders || d.data || [];
        if (raw.length) { setTenders(raw.map(normTender)); }
        else setTenders(SAMPLE_TENDERS);
      } else setTenders(SAMPLE_TENDERS);
    } catch { setTenders(SAMPLE_TENDERS); }
    try {
      const raw = await AsyncStorage.getItem('my_tenders');
      setMyTenders(raw ? JSON.parse(raw).map(normTender) : []);
      const rs = await AsyncStorage.getItem('saved_tenders');
      setSavedTenders(rs ? JSON.parse(rs) : []);
    } catch {}
  };

  const loadBids = async () => {
    try {
      const bizId = await getBizId();
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/company/data/bids?company_id=${bizId}&limit=200`,{headers:h});
      if (res.ok) {
        const d = await res.json();
        const all = (d.bids || d.data || []).map(normBid);
        if (all.length) {
          setSubmittedBids(all.filter(b=>b.status!=='draft'));
          setDraftBids(all.filter(b=>b.status==='draft'));
          return;
        }
      }
    } catch {}
    setSubmittedBids(SAMPLE_BIDS.filter(b=>b.status!=='draft'));
    setDraftBids(SAMPLE_BIDS.filter(b=>b.status==='draft'));
    try {
      const raw = await AsyncStorage.getItem('submitted_bids'); if(raw){const d=JSON.parse(raw);setSubmittedBids(d.map(normBid));}
      const rd  = await AsyncStorage.getItem('draft_bids');     if(rd) {const d=JSON.parse(rd); setDraftBids(d.map(normBid));}
    } catch {}
  };

  const loadQuotations = async () => {
    try {
      const bizId = await getBizId();
      const h = await getH();
      const res = await fetch(`${API_BASE}/api/company/data/quotations?company_id=${bizId}&limit=200`,{headers:h});
      if (res.ok) {
        const d = await res.json();
        const all = (d.quotations || d.data || []).map(normQuot);
        if (all.length) {
          setSentQuotations(all.filter(q=>q.status!=='draft'));
          setDraftQuotations(all.filter(q=>q.status==='draft'));
          return;
        }
      }
    } catch {}
    setSentQuotations(SAMPLE_QUOTATIONS.filter(q=>q.status!=='draft').map(normQuot));
    setDraftQuotations(SAMPLE_QUOTATIONS.filter(q=>q.status==='draft').map(normQuot));
    try {
      const rs = await AsyncStorage.getItem('sent_quotations');  if(rs){const d=JSON.parse(rs);setSentQuotations(d.map(normQuot));}
      const rd = await AsyncStorage.getItem('draft_quotations'); if(rd){const d=JSON.parse(rd);setDraftQuotations(d.map(normQuot));}
    } catch {}
  };
  const loadActivity = async () => {
    try { const raw=await AsyncStorage.getItem('tbq_activity'); setActivityLog(raw?JSON.parse(raw):[]);} catch {}
  };

  // ─── COMPUTED STATS ──────────────────────────────────────────────────────────
  const allBids  = [...submittedBids, ...draftBids];
  const allQuots = [...sentQuotations, ...draftQuotations];
  const wonBids  = submittedBids.filter(b=>b.status==='won');
  const bidPipe  = submittedBids.filter(b=>['submitted','under_review'].includes(b.status)).reduce((a,b)=>a+(b.amount||0),0);
  const wonRev   = wonBids.reduce((a,b)=>a+(b.amount||0),0);
  const accQuots = sentQuotations.filter(q=>q.status==='accepted');
  const quotWon  = accQuots.reduce((a,q)=>{
    const sub=q.items?.reduce((s,i)=>s+(i.total||0),0)||0;
    const disc=q.discountType==='percentage'?sub*(q.discountValue||0)/100:(q.discountValue||0);
    return a+(sub-disc)*(1+(q.vatRate||20)/100);
  },0);

  // ─── TENDER ACTIONS ──────────────────────────────────────────────────────────
  const toggleSaveTender = async (id) => {
    const ns = savedTenders.includes(id)?savedTenders.filter(s=>s!==id):[...savedTenders,id];
    setSavedTenders(ns);
    await AsyncStorage.setItem('saved_tenders',JSON.stringify(ns));
  };

  const getTendersForSubTab = () => {
    let base = tenderSubTab==='my' ? myTenders
      : tenderSubTab==='saved' ? tenders.filter(t=>savedTenders.includes(t.id))
      : tenderSubTab==='applied' ? tenders.filter(t=>allBids.some(b=>b.tenderId===t.id))
      : tenderSubTab==='expired' ? tenders.filter(t=>daysLeft(t.deadline)!=null&&daysLeft(t.deadline)<=0)
      : tenders.filter(t=>daysLeft(t.deadline)==null||daysLeft(t.deadline)>0);
    if (tenderSearch) base=base.filter(t=>(t.title+t.organisation+t.location).toLowerCase().includes(tenderSearch.toLowerCase()));
    if (tenderFilter.category!=='all') base=base.filter(t=>t.category===tenderFilter.category);
    if (tenderFilter.budgetMin) base=base.filter(t=>(t.budgetMax||0)>=Number(tenderFilter.budgetMin));
    if (tenderFilter.budgetMax) base=base.filter(t=>(t.budgetMin||0)<=Number(tenderFilter.budgetMax));
    if (tenderFilter.location) base=base.filter(t=>(t.location||'').toLowerCase().includes(String(tenderFilter.location ?? '').toLowerCase()));
    if (tenderFilter.bond) base=base.filter(t=>t.bond);
    return base.sort((a,b)=>{
      if(tenderSort==='deadline') return new Date(a.deadline||'9999')-new Date(b.deadline||'9999');
      if(tenderSort==='budget_h') return (b.budgetMax||0)-(a.budgetMax||0);
      if(tenderSort==='budget_l') return (a.budgetMin||0)-(b.budgetMin||0);
      if(tenderSort==='applicants') return (b.applicants||0)-(a.applicants||0);
      return new Date(b.createdDate||0)-new Date(a.createdDate||0);
    });
  };

  const getBidsForSubTab = () => {
    if(bidSubTab==='active') return submittedBids.filter(b=>['submitted','under_review'].includes(b.status));
    if(bidSubTab==='drafts') return draftBids;
    if(bidSubTab==='won')    return submittedBids.filter(b=>b.status==='won');
    if(bidSubTab==='lost')   return submittedBids.filter(b=>['lost','withdrawn'].includes(b.status));
    return allBids;
  };

  const getQuotsForSubTab = () => {
    if(quotSubTab==='active')   return sentQuotations.filter(q=>['sent','viewed'].includes(q.status));
    if(quotSubTab==='drafts')   return draftQuotations;
    if(quotSubTab==='accepted') return sentQuotations.filter(q=>['accepted','invoiced'].includes(q.status));
    if(quotSubTab==='expired')  return sentQuotations.filter(q=>['rejected','expired'].includes(q.status));
    return allQuots;
  };

  // ─── SAVE MY TENDER ──────────────────────────────────────────────────────────
  const saveMyTender = async (data, publish) => {
    try {
      const bizId = await getBizId();
      const h = await getH();
      const payload = {
        ...data,
        company_id:  bizId,
        status:       publish ? 'published' : 'draft',
        createdDate:  data.createdDate || new Date().toISOString(),
        // snake_case aliases for DB
        budget_min:   data.budgetMin,
        budget_max:   data.budgetMax,
        bond_amount:  data.bondAmount,
      };
      if (data.id) {
        await fetch(`${API_BASE}/api/company/data/tenders/${data.id}`,{method:'PUT',headers:h,body:JSON.stringify(payload)});
        const updated = myTenders.map(t=>t.id===data.id?payload:t);
        setMyTenders(updated); await AsyncStorage.setItem('my_tenders',JSON.stringify(updated));
      } else {
        payload.id = uid();
        try { await fetch(`${API_BASE}/api/company/data/tenders`,{method:'POST',headers:h,body:JSON.stringify(payload)}); } catch {}
        const updated = [payload,...myTenders];
        setMyTenders(updated); await AsyncStorage.setItem('my_tenders',JSON.stringify(updated));
      }
      await logActivity('tender', publish?`Published tender: ${data.title}`:`Saved tender draft: ${data.title}`, data.id);
    } catch(e){ Alert.alert('Error','Failed to save tender.'); }
  };

  const deleteMyTender = async (id) => {
    Alert.alert('Delete Tender','Are you sure?',[{text:'Cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
      try {
        const h=await getH();
        await fetch(`${API_BASE}/api/company/data/tenders/${id}`,{method:'DELETE',headers:h});
      } catch {}
      const updated=myTenders.filter(t=>t.id!==id);
      setMyTenders(updated); await AsyncStorage.setItem('my_tenders',JSON.stringify(updated));
      await logActivity('tender','Deleted tender',id);
    }}]);
  };

  // ─── BID ACTIONS ─────────────────────────────────────────────────────────────
  const saveBid = async (data, submit) => {
    try {
      const bizId = await getBizId(); const h=await getH();
      const payload = {
        ...data,
        company_id:   bizId,
        status:        submit ? 'submitted' : 'draft',
        submittedDate: submit ? new Date().toISOString() : data.submittedDate,
        // snake_case aliases for DB
        tender_id:     data.tenderId,
        tender_title:  data.tenderTitle,
        bond_amount:   data.bondAmount,
        validity_days: data.validityDays,
        submitted_date:submit ? new Date().toISOString() : data.submittedDate,
        payment_terms: data.paymentTerms,
      };
      if (data.id) {
        try { await fetch(`${API_BASE}/api/company/data/bids/${data.id}`,{method:'PUT',headers:h,body:JSON.stringify(payload)}); } catch {}
        if (submit) {
          const upd=draftBids.filter(b=>b.id!==data.id);
          setDraftBids(upd); await AsyncStorage.setItem('draft_bids',JSON.stringify(upd));
          const upd2=[payload,...submittedBids]; setSubmittedBids(upd2); await AsyncStorage.setItem('submitted_bids',JSON.stringify(upd2));
        } else {
          const upd=draftBids.map(b=>b.id===data.id?payload:b);
          setDraftBids(upd); await AsyncStorage.setItem('draft_bids',JSON.stringify(upd));
        }
      } else {
        payload.id=uid();
        try { await fetch(`${API_BASE}/api/company/data/bids`,{method:'POST',headers:h,body:JSON.stringify(payload)}); } catch {}
        if (submit) {
          const upd=[payload,...submittedBids]; setSubmittedBids(upd); await AsyncStorage.setItem('submitted_bids',JSON.stringify(upd));
        } else {
          const upd=[payload,...draftBids]; setDraftBids(upd); await AsyncStorage.setItem('draft_bids',JSON.stringify(upd));
        }
      }
      await logActivity('bid', submit?`Submitted bid: ${data.tenderTitle}`:`Saved bid draft: ${data.tenderTitle}`, data.id);
      Alert.alert('Success', submit?'Bid submitted successfully!':'Bid saved as draft.');
    } catch { Alert.alert('Error','Failed to save bid.'); }
  };

  const cloneBid = async (bid) => {
    const clone={...bid,id:uid(),status:'draft',submittedDate:null,tenderTitle:bid.tenderTitle+' (Copy)'};
    const upd=[clone,...draftBids]; setDraftBids(upd); await AsyncStorage.setItem('draft_bids',JSON.stringify(upd));
    await logActivity('bid',`Cloned bid: ${bid.tenderTitle}`,bid.id);
    Alert.alert('Cloned','Bid copied to Drafts.');
  };

  const withdrawBid = async (bid) => {
    Alert.alert('Withdraw Bid','Are you sure?',[{text:'Cancel'},{text:'Withdraw',style:'destructive',onPress:async()=>{
      const updated={...bid,status:'withdrawn'};
      try { const h=await getH(); await fetch(`${API_BASE}/api/company/data/bids/${bid.id}`,{method:'PUT',headers:h,body:JSON.stringify(updated)}); } catch {}
      const upd=submittedBids.map(b=>b.id===bid.id?updated:b); setSubmittedBids(upd); await AsyncStorage.setItem('submitted_bids',JSON.stringify(upd));
      await logActivity('bid',`Withdrew bid: ${bid.tenderTitle}`,bid.id);
    }}]);
  };

  const deleteDraftBid = async (id) => {
    Alert.alert('Delete Draft','Are you sure?',[{text:'Cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
      const upd=draftBids.filter(b=>b.id!==id); setDraftBids(upd); await AsyncStorage.setItem('draft_bids',JSON.stringify(upd));
      await logActivity('bid','Deleted bid draft',id);
    }}]);
  };

  // ─── QUOTATION ACTIONS ───────────────────────────────────────────────────────
  const saveQuotation = async (data, send) => {
    try {
      const bizId=await getBizId(); const h=await getH();
      const payload = {
        ...data,
        company_id:      bizId,
        status:           send ? 'sent' : 'draft',
        quotationNumber:  data.quotationNumber || newQUO(),
        createdDate:      data.createdDate || new Date().toISOString(),
        // snake_case aliases for DB
        quotation_number: data.quotationNumber || newQUO(),
        client_name:      data.clientName,
        client_company:   data.clientCompany,
        client_email:     data.clientEmail,
        client_phone:     data.clientPhone,
        client_address:   data.clientAddress,
        valid_until:      data.validUntil,
        created_date:     data.createdDate || new Date().toISOString(),
        vat_rate:         data.vatRate,
        discount_type:    data.discountType,
        discount_value:   data.discountValue,
        payment_terms:    data.paymentTerms,
      };
      if (data.id) {
        try { await fetch(`${API_BASE}/api/company/data/quotations/${data.id}`,{method:'PUT',headers:h,body:JSON.stringify(payload)}); } catch {}
        if (send) {
          const ud=draftQuotations.filter(q=>q.id!==data.id); setDraftQuotations(ud); await AsyncStorage.setItem('draft_quotations',JSON.stringify(ud));
          const us=[payload,...sentQuotations]; setSentQuotations(us); await AsyncStorage.setItem('sent_quotations',JSON.stringify(us));
        } else {
          const ud=draftQuotations.map(q=>q.id===data.id?payload:q); setDraftQuotations(ud); await AsyncStorage.setItem('draft_quotations',JSON.stringify(ud));
        }
      } else {
        payload.id=uid();
        try { await fetch(`${API_BASE}/api/company/data/quotations`,{method:'POST',headers:h,body:JSON.stringify(payload)}); } catch {}
        if (send) {
          const us=[payload,...sentQuotations]; setSentQuotations(us); await AsyncStorage.setItem('sent_quotations',JSON.stringify(us));
        } else {
          const ud=[payload,...draftQuotations]; setDraftQuotations(ud); await AsyncStorage.setItem('draft_quotations',JSON.stringify(ud));
        }
      }
      await logActivity('quotation', send?`Sent quotation: ${data.title}`:`Saved quotation draft: ${data.title}`, data.id);
      Alert.alert('Success', send?'Quotation sent!':'Quotation saved as draft.');
    } catch { Alert.alert('Error','Failed to save quotation.'); }
  };

  const updateQuotStatus = async (id, status) => {
    try {
      const h=await getH();
      const item=sentQuotations.find(q=>q.id===id);
      if(!item) return;
      const updated={...item,status};
      try { await fetch(`${API_BASE}/api/company/data/quotations/${id}`,{method:'PUT',headers:h,body:JSON.stringify(updated)}); } catch {}
      const upd=sentQuotations.map(q=>q.id===id?updated:q); setSentQuotations(upd); await AsyncStorage.setItem('sent_quotations',JSON.stringify(upd));
      await logActivity('quotation',`Quotation ${status}: ${item.title}`,id);
    } catch {}
  };

  const duplicateQuotation = async (quot) => {
    const dup={...quot,id:uid(),quotationNumber:newQUO(),status:'draft',createdDate:new Date().toISOString(),title:quot.title+' (Copy)'};
    const ud=[dup,...draftQuotations]; setDraftQuotations(ud); await AsyncStorage.setItem('draft_quotations',JSON.stringify(ud));
    await logActivity('quotation',`Duplicated quotation: ${quot.title}`,quot.id);
    Alert.alert('Duplicated','Quotation copied to Drafts.');
  };

  const sendReminder = async (quot) => {
    try {
      await Share.share({message:`Reminder: Your quotation ${quot.quotationNumber} for "${quot.title}" is awaiting your response. Valid until ${fmtDate(quot.validUntil)}. Please review and let us know if you have any questions.`});
      await logActivity('quotation',`Sent reminder for: ${quot.title}`,quot.id);
    } catch {}
  };

  const deleteQuotation = async (id, status) => {
    Alert.alert('Delete Quotation','Are you sure?',[{text:'Cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
      try { const h=await getH(); await fetch(`${API_BASE}/api/company/data/quotations/${id}`,{method:'DELETE',headers:h}); } catch {}
      if(status==='draft') {
        const ud=draftQuotations.filter(q=>q.id!==id); setDraftQuotations(ud); await AsyncStorage.setItem('draft_quotations',JSON.stringify(ud));
      } else {
        const us=sentQuotations.filter(q=>q.id!==id); setSentQuotations(us); await AsyncStorage.setItem('sent_quotations',JSON.stringify(us));
      }
      await logActivity('quotation','Deleted quotation',id);
    }}]);
  };

  // ─── COMPANY DETAILS HELPER ──────────────────────────────────────────────────
  const getCompanyDetails = async () => {
    try {
      const raw = await AsyncStorage.getItem('company_profile');
      if (!raw) return {};
      const p = JSON.parse(raw);
      const logo = p.logo||p.logo_url||p.company_logo||p.banner_image_url||'';
      return {
        name:    p.company_name||p.name||'JOBENG',
        logo:    logo ? (logo.startsWith('http') || logo.startsWith('data:') ? logo : `${API_BASE}${logo}`) : '',
        address: p.address||p.company_address||'',
        email:   p.email||p.company_email||'',
        phone:   p.phone||p.company_phone||'',
        website: p.website||p.company_website||'',
        regNo:   p.registration_number||p.reg_number||p.company_number||'',
        vatNo:   p.vat_number||p.vat_no||'',
      };
    } catch { return {}; }
  };

  const buildCompanyHeader = (co) => `
    <div class="co-header">
      <div class="co-left">
        ${co.logo ? `<img src="${co.logo}" class="co-logo" onerror="this.style.display='none'"/>` : ''}
        <div class="co-info">
          <div class="co-name">${co.name||'JOBENG'}</div>
          ${co.address ? `<div class="co-detail">${co.address}</div>` : ''}
          ${co.email   ? `<div class="co-detail">${co.email}</div>` : ''}
          ${co.phone   ? `<div class="co-detail">${co.phone}</div>` : ''}
          ${co.website ? `<div class="co-detail">${co.website}</div>` : ''}
        </div>
      </div>
      <div class="co-right">
        ${co.regNo ? `<div class="co-meta"><span>Reg No:</span> ${co.regNo}</div>` : ''}
        ${co.vatNo ? `<div class="co-meta"><span>VAT No:</span> ${co.vatNo}</div>` : ''}
        <div class="co-meta co-date"><span>Date:</span> ${new Date().toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}</div>
      </div>
    </div>`;

  const CO_HEADER_CSS = `
    .co-header{display:flex;justify-content:space-between;align-items:flex-start;padding:20px 32px 16px;background:#fff;border-bottom:3px solid #0e4e96;}
    .co-left{display:flex;align-items:center;gap:16px;}
    .co-logo{width:64px;height:64px;object-fit:contain;border-radius:10px;border:1px solid #e2e8f0;}
    .co-name{font-size:20px;font-weight:800;color:#0e4e96;margin-bottom:4px;letter-spacing:-0.3px;}
    .co-detail{font-size:12px;color:#64748b;margin:1px 0;}
    .co-right{text-align:right;}
    .co-meta{font-size:12px;color:#64748b;margin:2px 0;}
    .co-meta span{font-weight:600;color:#1e293b;}
    .co-date{margin-top:6px;}
  `;

  // ─── PDF GENERATORS ──────────────────────────────────────────────────────────
  const exportBidPDF = async (bid) => {
    try {
      const co = await getCompanyDetails();
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
        *{box-sizing:border-box;}
        body{font-family:Arial,sans-serif;margin:0;padding:0;color:#1e293b;}
        ${CO_HEADER_CSS}
        .header{background:linear-gradient(135deg,#0e4e96,#1e7be2);padding:28px 32px;color:white;}
        .header h1{margin:0;font-size:24px;font-weight:800;} .header p{margin:4px 0;opacity:0.85;font-size:13px;}
        .badge{display:inline-block;background:rgba(255,255,255,0.2);border-radius:12px;padding:4px 12px;font-size:12px;margin-top:8px;}
        .body{padding:24px 32px;} .section{margin-bottom:20px;border-bottom:1px solid #e2e8f0;padding-bottom:16px;}
        .section h2{color:#0e4e96;font-size:13px;margin-bottom:10px;text-transform:uppercase;letter-spacing:1px;font-weight:700;}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
        .item{background:#f8fafc;border-radius:10px;padding:12px;border:1px solid #f0f4f8;}
        .item label{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:0.5px;display:block;margin-bottom:3px;}
        .item p{margin:0;font-weight:700;color:#1e293b;font-size:14px;}
        .amount-box{background:linear-gradient(135deg,#0e4e96,#1e7be2);color:white;border-radius:14px;padding:20px;text-align:center;margin-bottom:24px;}
        .amount-box .amt{font-size:36px;font-weight:900;letter-spacing:-1px;}
        .amount-box .sub{font-size:12px;opacity:0.75;margin-top:6px;}
        table{width:100%;border-collapse:collapse;font-size:13px;}
        th{background:#0e4e96;color:white;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;}
        td{padding:10px 12px;border-bottom:1px solid #f0f4f8;}
        tr:nth-child(even) td{background:#f8fafc;}
        .chip{display:inline-block;background:#eff6ff;color:#3b82f6;border-radius:20px;padding:4px 12px;font-size:11px;margin:3px;font-weight:600;border:1px solid #bfdbfe;}
        .footer{background:#f8fafc;padding:16px 32px;text-align:center;font-size:11px;color:#94a3b8;margin-top:20px;border-top:1px solid #e2e8f0;}
        .footer strong{color:#0e4e96;}
        @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
      </style></head><body>
      ${buildCompanyHeader(co)}
      <div class="header">
        <h1>${bid.tenderTitle||'Tender Bid'}</h1>
        <p>${bid.organisation||''}</p>
        <span class="badge">${(bid.status||'draft').replace(/_/g,' ').toUpperCase()}</span>
      </div>
      <div class="body">
        <div class="amount-box"><div style="font-size:12px;opacity:0.8;margin-bottom:6px;text-transform:uppercase;letter-spacing:1px;">Bid Amount</div><div class="amt">${fmtCur(bid.amount,bid.currency||'GBP')}</div><div class="sub">Timeline: ${bid.timeline||'TBC'} &nbsp;·&nbsp; Payment: ${bid.paymentTerms||'TBC'}</div></div>
        <div class="section"><h2>Bid Summary</h2><div class="grid">
          <div class="item"><label>Currency</label><p>${bid.currency||'GBP'}</p></div>
          <div class="item"><label>Validity</label><p>${bid.validityDays||30} days</p></div>
          <div class="item"><label>Bond Amount</label><p>${fmtCur(bid.bondAmount||0,bid.currency||'GBP')}</p></div>
          <div class="item"><label>Submitted</label><p>${fmtDate(bid.submittedDate)}</p></div>
        </div></div>
        ${bid.proposal?`<div class="section"><h2>Executive Proposal</h2><p style="line-height:1.8;font-size:13px;color:#475569;">${bid.proposal}</p></div>`:''}
        ${bid.deliverables?.length?`<div class="section"><h2>Deliverables</h2><ul style="padding-left:18px;">${bid.deliverables.map(d=>`<li style="margin:6px 0;font-size:13px;">${d}</li>`).join('')}</ul></div>`:''}
        ${bid.team?.length?`<div class="section"><h2>Project Team</h2><table><thead><tr><th>Name</th><th>Role</th><th>Experience</th></tr></thead><tbody>${bid.team.map(m=>`<tr><td>${m.name}</td><td>${m.role}</td><td>${m.experience}</td></tr>`).join('')}</tbody></table></div>`:''}
        ${bid.certifications?.length?`<div class="section"><h2>Certifications</h2><div style="margin-top:6px;">${bid.certifications.map(c=>`<span class="chip">${c}</span>`).join('')}</div></div>`:''}
        ${bid.previousExperience?`<div class="section"><h2>Previous Experience</h2><p style="line-height:1.8;font-size:13px;color:#475569;">${bid.previousExperience}</p></div>`:''}
        ${bid.additionalNotes?`<div class="section"><h2>Additional Notes</h2><p style="font-size:13px;color:#475569;">${bid.additionalNotes}</p></div>`:''}
      </div>
      <div class="footer">Generated by <strong>${co.name||'JOBENG'}</strong> &nbsp;·&nbsp; ${new Date().toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} &nbsp;·&nbsp; Confidential</div>
      </body></html>`;
      const { uri } = await Print.printToFileAsync({ html, base64:false });
      await Sharing.shareAsync(uri, { mimeType:'application/pdf', dialogTitle:`Bid – ${bid.tenderTitle}` });
    } catch(e){ Alert.alert('Error','PDF generation failed: '+e.message); }
  };

  const exportQuotPDF = async (quot) => {
    try {
      const co = await getCompanyDetails();
      const sub=quot.items?.reduce((a,i)=>a+(i.total||0),0)||0;
      const disc=quot.discountType==='percentage'?sub*(quot.discountValue||0)/100:(quot.discountValue||0);
      const tax=(sub-disc)*(quot.vatRate||20)/100;
      const total=sub-disc+tax;
      const html=`<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
        *{box-sizing:border-box;}
        body{font-family:Arial,sans-serif;margin:0;padding:0;color:#1e293b;}
        ${CO_HEADER_CSS}
        .header{background:linear-gradient(135deg,#0e4e96,#1e7be2);padding:24px 32px;color:white;display:flex;justify-content:space-between;align-items:flex-start;}
        .header h1{margin:0;font-size:22px;font-weight:800;} .header p{margin:3px 0;opacity:0.85;font-size:12px;}
        .quot-num{background:rgba(255,255,255,0.2);border-radius:10px;padding:10px 18px;text-align:right;min-width:160px;}
        .body{padding:24px 32px;}
        .client-section{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:24px;}
        .box{background:#f8fafc;border-radius:12px;padding:16px;border:1px solid #f0f4f8;}
        .box h3{margin:0 0 10px;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#0e4e96;font-weight:700;}
        .box p{margin:2px 0;font-size:13px;color:#1e293b;}
        table{width:100%;border-collapse:collapse;margin-bottom:20px;}
        th{background:#0e4e96;color:white;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;}
        td{padding:10px 12px;border-bottom:1px solid #f0f4f8;font-size:13px;}
        tr:nth-child(even) td{background:#f8fafc;}
        .totals{float:right;width:300px;background:#f8fafc;border-radius:12px;padding:16px;border:1px solid #f0f4f8;}
        .tot-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px;}
        .tot-row.grand{border-top:2px solid #0e4e96;padding-top:12px;margin-top:8px;font-weight:800;font-size:17px;color:#0e4e96;}
        .section{margin:16px 0;border-top:1px solid #f0f4f8;padding-top:14px;clear:both;}
        .section h3{color:#0e4e96;font-size:11px;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px;font-weight:700;}
        .sig{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:36px;}
        .sig-line{border-top:2px solid #1e293b;padding-top:8px;font-size:11px;color:#64748b;font-weight:500;}
        .footer{background:#f8fafc;padding:14px 32px;text-align:center;font-size:11px;color:#94a3b8;margin-top:20px;border-top:1px solid #e2e8f0;clear:both;}
        .footer strong{color:#0e4e96;}
        @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
      </style></head><body>
      ${buildCompanyHeader(co)}
      <div class="header">
        <div><h1>${quot.title||'Quotation'}</h1><p>${quot.description||''}</p></div>
        <div class="quot-num"><div style="font-size:10px;opacity:0.75;text-transform:uppercase;letter-spacing:1px;">Quotation</div><div style="font-size:20px;font-weight:800;margin:4px 0;">${quot.quotationNumber||''}</div><div style="font-size:11px;opacity:0.75;">${fmtDate(quot.createdDate)}</div></div>
      </div>
      <div class="body">
        <div class="client-section">
          <div class="box"><h3>Prepared For</h3><p><strong>${quot.clientName||''}</strong></p><p>${quot.clientCompany||''}</p><p>${quot.clientEmail||''}</p><p>${quot.clientPhone||''}</p><p>${quot.clientAddress||''}</p></div>
          <div class="box"><h3>Prepared By</h3><p><strong>${co.name||'JOBENG'}</strong></p>${co.address?`<p>${co.address}</p>`:''}${co.email?`<p>${co.email}</p>`:''}${co.phone?`<p>${co.phone}</p>`:''}${co.regNo?`<p>Reg: ${co.regNo}</p>`:''}${co.vatNo?`<p>VAT: ${co.vatNo}</p>`:''}</div>
        </div>
        <div class="box" style="margin-bottom:20px;"><h3>Quotation Details</h3><p><strong>Valid Until:</strong> ${fmtDate(quot.validUntil)} &nbsp;&nbsp; <strong>Currency:</strong> ${quot.currency||'GBP'} &nbsp;&nbsp; <strong>Payment:</strong> ${quot.paymentTerms||'30 days net'} &nbsp;&nbsp; <strong>VAT:</strong> ${quot.vatRate||20}%</p></div>
        <table>
          <thead><tr><th>Description</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr></thead>
          <tbody>${(quot.items||[]).map(i=>`<tr><td>${i.description}</td><td>${i.quantity}</td><td>${i.unit||''}</td><td>${fmtCur(i.unitPrice,quot.currency)}</td><td>${fmtCur(i.total,quot.currency)}</td></tr>`).join('')}</tbody>
        </table>
        <div class="totals">
          <div class="tot-row"><span>Subtotal:</span><span>${fmtCur(sub,quot.currency)}</span></div>
          ${disc>0?`<div class="tot-row"><span>Discount (${quot.discountType==='percentage'?quot.discountValue+'%':fmtCur(disc,quot.currency)}):</span><span style="color:#059669;">-${fmtCur(disc,quot.currency)}</span></div>`:''}
          <div class="tot-row"><span>VAT (${quot.vatRate||20}%):</span><span>${fmtCur(tax,quot.currency)}</span></div>
          <div class="tot-row grand"><span>TOTAL:</span><span>${fmtCur(total,quot.currency)}</span></div>
        </div>
        <div style="clear:both;"></div>
        ${quot.notes?`<div class="section"><h3>Notes</h3><p style="font-size:13px;line-height:1.7;color:#475569;">${quot.notes}</p></div>`:''}
        ${quot.termsConditions?`<div class="section"><h3>Terms & Conditions</h3><p style="font-size:12px;line-height:1.7;color:#475569;">${quot.termsConditions}</p></div>`:''}
        <div class="sig">
          <div><div style="height:48px;"></div><div class="sig-line">Authorised Signature – ${co.name||'JOBENG'}</div></div>
          <div><div style="height:48px;"></div><div class="sig-line">Client Acceptance – ${quot.clientName||'Client'}</div></div>
        </div>
      </div>
      <div class="footer">Generated by <strong>${co.name||'JOBENG'}</strong> &nbsp;·&nbsp; ${new Date().toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} &nbsp;·&nbsp; Confidential document</div>
      </body></html>`;
      const { uri }=await Print.printToFileAsync({html,base64:false});
      await Sharing.shareAsync(uri,{mimeType:'application/pdf',dialogTitle:`Quotation – ${quot.quotationNumber}`});
    } catch(e){ Alert.alert('Error','PDF generation failed: '+e.message); }
  };

  // ─── TABS ─────────────────────────────────────────────────────────────────────
  const renderMainTabs = () => (
    <View style={ss.mainTabBar}>
      {[
        ['tenders',   'file-search',           'Tenders'],
        ['bids',      'send',                  'Bids'],
        ['quotations','file-document-outline', 'Quotations'],
        ['uk_live',   'web',                   'UK Live'],
        ['analytics', 'chart-bar',             'Analytics'],
      ].map(([t,ic,lb])=>(
        <TouchableOpacity key={t} style={[ss.mainTab,mainTab===t&&ss.mainTabActive]} onPress={()=>setMainTab(t)}>
          <MaterialCommunityIcons name={ic} size={15} color={mainTab===t?P:'#94a3b8'}/>
          <Text style={[ss.mainTabTxt,mainTab===t&&ss.mainTabTxtActive]}>{lb}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

      <View style={{height:80,backgroundColor:'#fff',borderBottomWidth:1,borderBottomColor:'#e8edf2'}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:16,paddingVertical:8,gap:10,alignItems:'center'}}>
          {[
            {l:'Active Tenders',v:tenders.filter(t=>daysLeft(t.deadline)>0).length,ic:'file-search',c:'#0e4e96'},
            {l:'Active Bids',v:submittedBids.filter(b=>['submitted','under_review'].includes(b.status)).length,ic:'send',c:P2},
            {l:'Bids Won',v:wonBids.length,ic:'trophy',c:'#059669'},
            {l:'Win Rate',v:(allBids.filter(b=>b.status!=='draft').length>0?Math.round(wonBids.length/allBids.filter(b=>b.status!=='draft').length*100):0)+'%',ic:'percent',c:'#d97706'},
            {l:'Bid Pipeline',v:fmtGBP(bidPipe),ic:'cash',c:'#0e4e96'},
            {l:'Won Revenue',v:fmtGBP(wonRev),ic:'bank',c:'#059669'},
            {l:'Quotes',v:allQuots.length,ic:'file-document',c:P2},
            {l:'Quote Won',v:fmtGBP(quotWon),ic:'check-decagram',c:'#8b5cf6'},
          ].map((s,i)=>(
            <View key={i} style={[ss.statCard,{borderLeftColor:s.c}]}>
              <MaterialCommunityIcons name={s.ic} size={14} color={s.c}/>
              <Text style={[ss.statVal,{color:s.c}]}>{s.v}</Text>
              <Text style={ss.statLbl}>{s.l}</Text>
            </View>
          ))}
        </ScrollView>
      </View>

  // ─── TENDERS TAB ─────────────────────────────────────────────────────────────
  const renderTendersTab = () => {
    const subTabs = [['available','Available'],['my','My Tenders'],['applied','Applied'],['saved','Saved'],['expired','Expired']];
    const items = getTendersForSubTab();
    return (
      <View style={{flex:1}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={ss.subTabBar} contentContainerStyle={{paddingHorizontal:16,paddingVertical:0,gap:8,alignItems:'center',height:52}}>
          {subTabs.map(([t,l])=>(
            <TouchableOpacity key={t} style={[ss.subTab,tenderSubTab===t&&ss.subTabActive]} onPress={()=>setTenderSubTab(t)}>
              <Text style={[ss.subTabTxt,tenderSubTab===t&&ss.subTabTxtActive]}>{l}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <View style={ss.searchRow}>
          <View style={ss.searchBox}><MaterialCommunityIcons name="magnify" size={18} color="#94a3b8"/><TextInput style={ss.searchInput} placeholder="Search tenders..." value={tenderSearch} onChangeText={setTenderSearch} placeholderTextColor="#94a3b8"/></View>
          <TouchableOpacity style={ss.iconBtn} onPress={()=>setShowTenderFilter(true)}><MaterialCommunityIcons name="filter-variant" size={20} color={P}/></TouchableOpacity>
          <TouchableOpacity style={ss.iconBtn} onPress={()=>setShowTenderSort(true)}><MaterialCommunityIcons name="sort" size={20} color={P}/></TouchableOpacity>
          {tenderSubTab==='my'&&<TouchableOpacity style={ss.addBtn} onPress={()=>{setEditingMyTender(null);setShowMyTenderForm(true);}}><MaterialCommunityIcons name="plus" size={18} color="#fff"/><Text style={ss.addBtnTxt}>Post</Text></TouchableOpacity>}
        </View>
        {tenderSubTab==='my' && items.length===0 ? (
          <View style={ss.empty}><MaterialCommunityIcons name="file-plus-outline" size={48} color="#cbd5e1"/><Text style={ss.emptyTxt}>No tenders posted yet</Text><TouchableOpacity style={ss.emptyBtn} onPress={()=>{setEditingMyTender(null);setShowMyTenderForm(true);}}><Text style={ss.emptyBtnTxt}>Post a Tender</Text></TouchableOpacity></View>
        ) : items.length===0 ? (
          <View style={ss.empty}><MaterialCommunityIcons name="file-search-outline" size={48} color="#cbd5e1"/><Text style={ss.emptyTxt}>No tenders found</Text></View>
        ) : (
          <FlatList data={items} keyExtractor={i=>i.id} contentContainerStyle={{padding:16,gap:12}} renderItem={({item})=>(
            tenderSubTab==='my' ? (
              <View style={ss.card}>
                <View style={ss.cardRow}>
                  <View style={[ss.catIcon,{backgroundColor:item.status==='published'?'#059669':'#64748B'}]}><MaterialCommunityIcons name="office-building" size={18} color="#fff"/></View>
                  <View style={{flex:1,marginLeft:10}}>
                    <Text style={ss.cardTitle}>{item.title}</Text>
                    <Text style={ss.cardSub}>{item.location} · {item.category}</Text>
                  </View>
                  <Pill label={item.status||'draft'} color={item.status==='published'?'#059669':'#64748B'}/>
                </View>
                <View style={[ss.cardRow,{marginTop:8,gap:12}]}>
                  <View style={ss.metaItem}><MaterialCommunityIcons name="currency-gbp" size={14} color={P}/><Text style={ss.metaTxt}>{fmtGBP(item.budgetMin)}–{fmtGBP(item.budgetMax)}</Text></View>
                  <View style={ss.metaItem}><MaterialCommunityIcons name="calendar" size={14} color="#64748B"/><Text style={ss.metaTxt}>{fmtDate(item.deadline)}</Text></View>
                </View>
                <View style={[ss.cardRow,{marginTop:8,gap:8}]}>
                  <TouchableOpacity style={ss.acBtn} onPress={()=>{setEditingMyTender(item);setShowMyTenderForm(true);}}><MaterialCommunityIcons name="pencil" size={14} color={P}/><Text style={[ss.acTxt,{color:P}]}>Edit</Text></TouchableOpacity>
                  <TouchableOpacity style={ss.acBtn} onPress={()=>deleteMyTender(item.id)}><MaterialCommunityIcons name="delete" size={14} color="#dc2626"/><Text style={[ss.acTxt,{color:'#dc2626'}]}>Delete</Text></TouchableOpacity>
                </View>
              </View>
            ) : (
              <TenderCard item={item} onPress={(t)=>{setSelectedTender(t);setShowTenderDetail(true);}} onSave={toggleSaveTender} saved={savedTenders.includes(item.id)}/>
            )
          )}/>
        )}
      </View>
    );
  };

  // ─── BIDS TAB ─────────────────────────────────────────────────────────────────
  const renderBidsTab = () => {
    const subTabs=[['active','Active'],['drafts','Drafts'],['won','Won'],['lost','Lost/Withdrawn'],['all','All']];
    const items=getBidsForSubTab();
    return (
      <View style={{flex:1}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={ss.subTabBar} contentContainerStyle={{paddingHorizontal:16,paddingVertical:0,gap:8,alignItems:'center',height:52}}>
          {subTabs.map(([t,l])=>(
            <TouchableOpacity key={t} style={[ss.subTab,bidSubTab===t&&ss.subTabActive]} onPress={()=>setBidSubTab(t)}>
              <Text style={[ss.subTabTxt,bidSubTab===t&&ss.subTabTxtActive]}>{l}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <View style={[ss.searchRow,{paddingTop:4}]}>
          <View style={{flex:1}}/>
          <TouchableOpacity style={ss.addBtn} onPress={()=>{setEditingBid(null);setShowBidForm(true);}}><MaterialCommunityIcons name="plus" size={18} color="#fff"/><Text style={ss.addBtnTxt}>New Bid</Text></TouchableOpacity>
        </View>
        {items.length===0 ? (
          <View style={ss.empty}><MaterialCommunityIcons name="send-outline" size={48} color="#cbd5e1"/><Text style={ss.emptyTxt}>No bids here yet</Text><TouchableOpacity style={ss.emptyBtn} onPress={()=>{setEditingBid(null);setShowBidForm(true);}}><Text style={ss.emptyBtnTxt}>Create Bid</Text></TouchableOpacity></View>
        ) : (
          <FlatList data={items} keyExtractor={i=>i.id} contentContainerStyle={{padding:16,gap:12}} renderItem={({item})=>(
            <BidCard item={item} onPress={(b)=>{setSelectedBid(b);setShowBidDetail(true);}} onEdit={(b)=>{setEditingBid(b);setShowBidForm(true);}} onClone={cloneBid} onPDF={exportBidPDF} onWithdraw={withdrawBid} onDelete={deleteDraftBid}/>
          )}/>
        )}
      </View>
    );
  };

  // ─── QUOTATIONS TAB ───────────────────────────────────────────────────────────
  const renderQuotationsTab = () => {
    const subTabs=[['active','Active'],['drafts','Drafts'],['accepted','Accepted'],['expired','Rejected/Expired'],['all','All']];
    const items=getQuotsForSubTab();
    return (
      <View style={{flex:1}}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={ss.subTabBar} contentContainerStyle={{paddingHorizontal:16,paddingVertical:0,gap:8,alignItems:'center',height:52}}>
          {subTabs.map(([t,l])=>(
            <TouchableOpacity key={t} style={[ss.subTab,quotSubTab===t&&ss.subTabActive]} onPress={()=>setQuotSubTab(t)}>
              <Text style={[ss.subTabTxt,quotSubTab===t&&ss.subTabTxtActive]}>{l}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <View style={[ss.searchRow,{paddingTop:4}]}>
          <View style={{flex:1}}/>
          <TouchableOpacity style={ss.addBtn} onPress={()=>{setEditingQuot(null);setShowQuotForm(true);}}><MaterialCommunityIcons name="plus" size={18} color="#fff"/><Text style={ss.addBtnTxt}>New Quote</Text></TouchableOpacity>
        </View>
        {items.length===0 ? (
          <View style={ss.empty}><MaterialCommunityIcons name="file-document-outline" size={48} color="#cbd5e1"/><Text style={ss.emptyTxt}>No quotations here</Text><TouchableOpacity style={ss.emptyBtn} onPress={()=>{setEditingQuot(null);setShowQuotForm(true);}}><Text style={ss.emptyBtnTxt}>Create Quotation</Text></TouchableOpacity></View>
        ) : (
          <FlatList data={items} keyExtractor={i=>i.id} contentContainerStyle={{padding:16,gap:12}} renderItem={({item})=>(
            <QuotCard item={item} onPress={(q)=>{setSelectedQuot(q);setShowQuotDetail(true);}} onPDF={exportQuotPDF} onDuplicate={duplicateQuotation} onReminder={sendReminder} onAccept={(id)=>updateQuotStatus(id,'accepted')} onReject={(id)=>updateQuotStatus(id,'rejected')} onInvoice={(id)=>updateQuotStatus(id,'invoiced')} onDelete={deleteQuotation}/>
          )}/>
        )}
      </View>
    );
  };

  // ─── ANALYTICS TAB ───────────────────────────────────────────────────────────
  const renderAnalyticsTab = () => {
    const bidByStatus = ['draft','submitted','under_review','won','lost','withdrawn'].map(s=>({s,v:allBids.filter(b=>b.status===s).length}));
    const quotByStatus = ['draft','sent','viewed','accepted','rejected','expired','invoiced'].map(s=>({s,v:allQuots.filter(q=>q.status===s).length}));
    const urgentTenders = tenders.filter(t=>{ const d=daysLeft(t.deadline); return d!=null&&d>0&&d<=7; });
    const expiringBids = submittedBids.filter(b=>{ if(!b.submittedDate||!b.validityDays) return false; const exp=new Date(new Date(b.submittedDate).getTime()+b.validityDays*86400000); const d=Math.ceil((exp-new Date())/86400000); return d>0&&d<=14; });
    const catBreakdown = CATEGORIES.filter(c=>c.id!=='all').map(c=>({c:c.label,v:allBids.filter(b=>b.tenderId&&tenders.find(t=>t.id===b.tenderId)?.category===c.id).length})).filter(x=>x.v>0);
    const maxBidBar=Math.max(...bidByStatus.map(x=>x.v),1);
    const maxQuotBar=Math.max(...quotByStatus.map(x=>x.v),1);
    const maxCatBar=Math.max(...catBreakdown.map(x=>x.v),1);
    const winRate=allBids.filter(b=>b.status!=='draft').length>0?Math.round(wonBids.length/allBids.filter(b=>b.status!=='draft').length*100):0;
    const accRate=sentQuotations.filter(q=>q.status!=='draft').length>0?Math.round(accQuots.length/sentQuotations.filter(q=>q.status!=='draft').length*100):0;
    return (
      <ScrollView style={{flex:1}} contentContainerStyle={{padding:16,gap:16}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={P}/>}>
        {/* KPI Grid */}
        <Text style={ss.secLabel}>Key Performance Indicators</Text>
        <View style={ss.kpiGrid}>
          {[
            {l:'Active Tenders',v:tenders.filter(t=>daysLeft(t.deadline)>0).length,c:P,ic:'file-search'},
            {l:'Active Bids',v:submittedBids.filter(b=>['submitted','under_review'].includes(b.status)).length,c:P2,ic:'send'},
            {l:'Bids Won',v:wonBids.length,c:'#059669',ic:'trophy'},
            {l:'Win Rate',v:winRate+'%',c:'#d97706',ic:'percent'},
            {l:'Bid Pipeline',v:fmtGBP(bidPipe),c:P,ic:'cash'},
            {l:'Won Revenue',v:fmtGBP(wonRev),c:'#059669',ic:'bank'},
            {l:'Quotes Sent',v:sentQuotations.length,c:P2,ic:'file-document'},
            {l:'Quote Accept',v:accRate+'%',c:'#8b5cf6',ic:'check-decagram'},
          ].map((k,i)=>(
            <View key={i} style={ss.kpiCard}>
              <View style={[ss.kpiIcon,{backgroundColor:k.c+'15'}]}><MaterialCommunityIcons name={k.ic} size={20} color={k.c}/></View>
              <Text style={[ss.kpiVal,{color:k.c}]}>{k.v}</Text>
              <Text style={ss.kpiLbl}>{k.l}</Text>
            </View>
          ))}
        </View>

        {/* Bid Pipeline Chart */}
        <View style={ss.chartCard}>
          <Text style={ss.chartTitle}>Bid Pipeline by Status</Text>
          <View style={{gap:8,marginTop:12}}>
            {bidByStatus.filter(x=>x.v>0).map(({s,v})=>(
              <View key={s}>
                <View style={ss.barRow}>
                  <Text style={ss.barLabel}>{s.replace(/_/g,' ')}</Text>
                  <Text style={[ss.barVal,{color:BID_STATUS_COLORS[s]}]}>{v}</Text>
                </View>
                <View style={ss.barBg}><View style={[ss.barFill,{width:`${(v/maxBidBar)*100}%`,backgroundColor:BID_STATUS_COLORS[s]}]}/></View>
              </View>
            ))}
          </View>
        </View>

        {/* Quotation Pipeline Chart */}
        <View style={ss.chartCard}>
          <Text style={ss.chartTitle}>Quotation Pipeline by Status</Text>
          <View style={{gap:8,marginTop:12}}>
            {quotByStatus.filter(x=>x.v>0).map(({s,v})=>(
              <View key={s}>
                <View style={ss.barRow}>
                  <Text style={ss.barLabel}>{s}</Text>
                  <Text style={[ss.barVal,{color:QUOT_STATUS_COLORS[s]}]}>{v}</Text>
                </View>
                <View style={ss.barBg}><View style={[ss.barFill,{width:`${(v/maxQuotBar)*100}%`,backgroundColor:QUOT_STATUS_COLORS[s]}]}/></View>
              </View>
            ))}
          </View>
        </View>

        {/* Urgent Tenders */}
        {urgentTenders.length>0&&(
          <View style={ss.chartCard}>
            <Text style={ss.chartTitle}>⚡ Urgent Tenders (closing in ≤7 days)</Text>
            <View style={{gap:8,marginTop:10}}>
              {urgentTenders.map(t=>(
                <TouchableOpacity key={t.id} style={ss.urgentRow} onPress={()=>{setSelectedTender(t);setShowTenderDetail(true);}}>
                  <View style={{flex:1}}><Text style={ss.urgentTitle}>{t.title}</Text><Text style={ss.urgentSub}>{t.organisation}</Text></View>
                  <View style={ss.urgentBadge}><Text style={ss.urgentBadgeTxt}>{daysLeft(t.deadline)}d left</Text></View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {/* Expiring Bids */}
        {expiringBids.length>0&&(
          <View style={ss.chartCard}>
            <Text style={ss.chartTitle}>⏰ Bids Expiring Soon (≤14 days validity)</Text>
            <View style={{gap:8,marginTop:10}}>
              {expiringBids.map(b=>{
                const exp=new Date(new Date(b.submittedDate).getTime()+b.validityDays*86400000);
                const d=Math.ceil((exp-new Date())/86400000);
                return (
                  <TouchableOpacity key={b.id} style={ss.urgentRow} onPress={()=>{setSelectedBid(b);setShowBidDetail(true);}}>
                    <View style={{flex:1}}><Text style={ss.urgentTitle}>{b.tenderTitle}</Text><Text style={ss.urgentSub}>{fmtCur(b.amount,b.currency)}</Text></View>
                    <View style={[ss.urgentBadge,{backgroundColor:'#fef3c7',borderColor:'#d97706'}]}><Text style={[ss.urgentBadgeTxt,{color:'#d97706'}]}>{d}d valid</Text></View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}

        {/* Category Breakdown */}
        {catBreakdown.length>0&&(
          <View style={ss.chartCard}>
            <Text style={ss.chartTitle}>Bids by Category</Text>
            <View style={{gap:8,marginTop:12}}>
              {catBreakdown.map(({c,v})=>(
                <View key={c}>
                  <View style={ss.barRow}><Text style={ss.barLabel}>{c}</Text><Text style={[ss.barVal,{color:P}]}>{v}</Text></View>
                  <View style={ss.barBg}><View style={[ss.barFill,{width:`${(v/maxCatBar)*100}%`,backgroundColor:P}]}/></View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Recent Activity */}
        <View style={ss.chartCard}>
          <Text style={ss.chartTitle}>Recent Activity</Text>
          {activityLog.length===0 ? <Text style={ss.emptyTxt}>No activity yet</Text> : (
            <View style={{gap:8,marginTop:10}}>
              {activityLog.slice(0,15).map(a=>(
                <View key={a.id} style={ss.actRow}>
                  <MaterialCommunityIcons name={a.type==='bid'?'send':a.type==='quotation'?'file-document':'file-search'} size={16} color={a.type==='bid'?P:a.type==='quotation'?'#8b5cf6':'#059669'}/>
                  <View style={{flex:1,marginLeft:8}}><Text style={ss.actMsg}>{a.message}</Text><Text style={ss.actDate}>{fmtDate(a.date)}</Text></View>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Quick Actions */}
        <Text style={ss.secLabel}>Quick Actions</Text>
        <View style={ss.qaGrid}>
          {[
            {l:'New Bid',ic:'send-circle',c:P,act:()=>{setEditingBid(null);setShowBidForm(true);setMainTab('bids');}},
            {l:'New Quote',ic:'file-document-plus',c:'#8b5cf6',act:()=>{setEditingQuot(null);setShowQuotForm(true);setMainTab('quotations');}},
            {l:'Post Tender',ic:'plus-circle',c:'#059669',act:()=>{setEditingMyTender(null);setShowMyTenderForm(true);setMainTab('tenders');setTenderSubTab('my');}},
            {l:'Browse Tenders',ic:'magnify',c:'#d97706',act:()=>{setMainTab('tenders');setTenderSubTab('available');}},
          ].map((q,i)=>(
            <TouchableOpacity key={i} style={ss.qaBtn} onPress={q.act}>
              <View style={[ss.qaIcon,{backgroundColor:q.c+'15'}]}><MaterialCommunityIcons name={q.ic} size={24} color={q.c}/></View>
              <Text style={[ss.qaTxt,{color:q.c}]}>{q.l}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>
    );
  };

  // ─── TENDER DETAIL MODAL ─────────────────────────────────────────────────────
  const renderTenderDetailModal = () => {
    if (!selectedTender) return null;
    const t=selectedTender; const dl=daysLeft(t.deadline);
    return (
      <Modal visible={showTenderDetail} animationType="slide" onRequestClose={()=>setShowTenderDetail(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          <View style={ss.modalHdr}>
            <TouchableOpacity onPress={()=>setShowTenderDetail(false)}><MaterialCommunityIcons name="arrow-left" size={24} color="#fff"/></TouchableOpacity>
            <Text style={ss.modalTitle} numberOfLines={1}>{t.title}</Text>
            <TouchableOpacity onPress={()=>toggleSaveTender(t.id)}><MaterialCommunityIcons name={savedTenders.includes(t.id)?'bookmark':'bookmark-outline'} size={24} color={savedTenders.includes(t.id)?Y:"rgba(255,255,255,0.8)"}/></TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>
            <View style={[ss.heroBox,{}]}>
              <View style={{backgroundColor:P,borderRadius:16,padding:20,marginBottom:16}}>
                <Text style={{color:'#fff',fontSize:18,fontWeight:'700',marginBottom:4}}>{t.title}</Text>
                <Text style={{color:'rgba(255,255,255,0.85)',fontSize:13}}>{t.organisation}</Text>
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginTop:12}}>
                  {t.tags?.map(tg=><View key={tg} style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:10,paddingHorizontal:10,paddingVertical:3}}><Text style={{color:'#fff',fontSize:11}}>{tg.toUpperCase()}</Text></View>)}
                  {t.bond&&<View style={{backgroundColor:'#fdb913',borderRadius:10,paddingHorizontal:10,paddingVertical:3}}><Text style={{color:P,fontSize:11,fontWeight:'700'}}>BOND REQUIRED</Text></View>}
                </View>
              </View>
              <View style={ss.grid2}>
                <View style={ss.detailBox}><Text style={ss.detailLbl}>Budget Range</Text><Text style={ss.detailVal}>{fmtGBP(t.budgetMin)} – {fmtGBP(t.budgetMax)}</Text></View>
                <View style={ss.detailBox}><Text style={ss.detailLbl}>Deadline</Text><Text style={[ss.detailVal,dl!=null&&dl<=7&&{color:'#dc2626'}]}>{dl!=null?(dl<=0?'Closed':`${dl} days left`):'—'}</Text></View>
                <View style={ss.detailBox}><Text style={ss.detailLbl}>Duration</Text><Text style={ss.detailVal}>{t.duration||'—'}</Text></View>
                <View style={ss.detailBox}><Text style={ss.detailLbl}>Location</Text><Text style={ss.detailVal}>{t.location}</Text></View>
                <View style={ss.detailBox}><Text style={ss.detailLbl}>Category</Text><Text style={ss.detailVal}>{t.category}</Text></View>
                <View style={ss.detailBox}><Text style={ss.detailLbl}>Applicants</Text><Text style={ss.detailVal}>{t.applicants||0}</Text></View>
              </View>
              {t.description&&<View style={ss.descBox}><Text style={ss.descLbl}>Description</Text><Text style={ss.descTxt}>{t.description}</Text></View>}
              {t.requirements?.length>0&&<View style={ss.descBox}><Text style={ss.descLbl}>Requirements</Text>{t.requirements.map((r,i)=><View key={i} style={ss.reqRow}><MaterialCommunityIcons name="check-circle" size={16} color={P}/><Text style={ss.reqTxt}>{r}</Text></View>)}</View>}
              {t.bond&&<View style={[ss.alertBox,{borderColor:'#d97706'}]}><MaterialCommunityIcons name="alert" size={18} color="#d97706"/><Text style={{color:'#d97706',marginLeft:8,flex:1,fontSize:13}}>Performance bond required: {fmtGBP(t.bondAmount)}</Text></View>}
              {t.contactEmail&&<View style={ss.descBox}><Text style={ss.descLbl}>Contact</Text><Text style={ss.descTxt}>{t.contactEmail}{t.contactPhone?` · ${t.contactPhone}`:''}</Text></View>}
            </View>
          </ScrollView>
          <View style={ss.modalFooter}>
            <TouchableOpacity style={ss.footerBtnSec} onPress={async()=>{ await Share.share({message:`Tender: ${t.title}
Org: ${t.organisation}
Budget: ${fmtGBP(t.budgetMin)}–${fmtGBP(t.budgetMax)}
Deadline: ${fmtDate(t.deadline)}`}); }}><MaterialCommunityIcons name="share-variant" size={18} color={P}/><Text style={[ss.footerBtnSecTxt,{color:P}]}>Share</Text></TouchableOpacity>
            <TouchableOpacity style={ss.footerBtnPri} onPress={()=>{ setShowTenderDetail(false); setTimeout(()=>{setEditingBid({tenderId:t.id,tenderTitle:t.title,organisation:t.organisation,currency:t.currency||'GBP'});setShowBidForm(true);setMainTab('bids');},300); }}><MaterialCommunityIcons name="send" size={18} color="#fff"/><Text style={ss.footerBtnPriTxt}>Create Bid</Text></TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── BID DETAIL MODAL ─────────────────────────────────────────────────────────
  const renderBidDetailModal = () => {
    if (!selectedBid) return null;
    const b=selectedBid; const col=BID_STATUS_COLORS[b.status]||'#64748B';
    return (
      <Modal visible={showBidDetail} animationType="slide" onRequestClose={()=>setShowBidDetail(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          <View style={ss.modalHdr}>
            <TouchableOpacity onPress={()=>setShowBidDetail(false)}><MaterialCommunityIcons name="arrow-left" size={24} color="#fff"/></TouchableOpacity>
            <Text style={ss.modalTitle}>Bid Details</Text>
            <TouchableOpacity onPress={()=>exportBidPDF(b)}><MaterialCommunityIcons name="file-pdf-box" size={24} color="#fff"/></TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>
            <View style={{backgroundColor:col,borderRadius:16,padding:20,marginBottom:16}}>
              <Text style={{color:'#fff',fontSize:18,fontWeight:'700',marginBottom:4}}>{b.tenderTitle}</Text>
              <Text style={{color:'rgba(255,255,255,0.85)',fontSize:13,marginBottom:10}}>{b.organisation}</Text>
              <View style={{flexDirection:'row',gap:12}}>
                <View style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:10,padding:10,flex:1,alignItems:'center'}}><Text style={{color:'#fff',fontSize:11,opacity:0.8}}>BID AMOUNT</Text><Text style={{color:'#fff',fontSize:20,fontWeight:'700'}}>{fmtCur(b.amount,b.currency)}</Text></View>
                <View style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:10,padding:10,flex:1,alignItems:'center'}}><Text style={{color:'#fff',fontSize:11,opacity:0.8}}>STATUS</Text><Text style={{color:'#fff',fontSize:14,fontWeight:'700'}}>{(b.status||'').replace(/_/g,' ').toUpperCase()}</Text></View>
              </View>
            </View>
            <View style={ss.grid2}>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Timeline</Text><Text style={ss.detailVal}>{b.timeline||'—'}</Text></View>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Validity</Text><Text style={ss.detailVal}>{b.validityDays||30} days</Text></View>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Payment Terms</Text><Text style={ss.detailVal}>{b.paymentTerms||'—'}</Text></View>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Bond</Text><Text style={ss.detailVal}>{fmtCur(b.bondAmount||0,b.currency)}</Text></View>
            </View>
            {b.proposal&&<View style={ss.descBox}><Text style={ss.descLbl}>Proposal</Text><Text style={ss.descTxt}>{b.proposal}</Text></View>}
            {b.deliverables?.length>0&&<View style={ss.descBox}><Text style={ss.descLbl}>Deliverables</Text>{b.deliverables.map((d,i)=><View key={i} style={ss.reqRow}><MaterialCommunityIcons name="check-circle" size={16} color={P}/><Text style={ss.reqTxt}>{d}</Text></View>)}</View>}
            {b.team?.length>0&&(
              <View style={ss.descBox}><Text style={ss.descLbl}>Project Team</Text>
                {b.team.map((m,i)=>(
                  <View key={i} style={ss.teamRow}><View style={ss.teamAvatar}><Text style={ss.teamAvatarTxt}>{(m.name||'?')[0]}</Text></View><View><Text style={ss.teamName}>{m.name}</Text><Text style={ss.teamRole}>{m.role} · {m.experience}</Text></View></View>
                ))}
              </View>
            )}
            {b.certifications?.length>0&&<View style={ss.descBox}><Text style={ss.descLbl}>Certifications</Text><View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:6}}>{b.certifications.map((c,i)=><View key={i} style={ss.certChip}><Text style={ss.certChipTxt}>{c}</Text></View>)}</View></View>}
            {b.previousExperience&&<View style={ss.descBox}><Text style={ss.descLbl}>Previous Experience</Text><Text style={ss.descTxt}>{b.previousExperience}</Text></View>}
            {b.additionalNotes&&<View style={ss.descBox}><Text style={ss.descLbl}>Notes</Text><Text style={ss.descTxt}>{b.additionalNotes}</Text></View>}
          </ScrollView>
          <View style={ss.modalFooter}>
            <TouchableOpacity style={ss.footerBtnSec} onPress={()=>{setShowBidDetail(false);setTimeout(()=>{setEditingBid(b);setShowBidForm(true);},300);}}><MaterialCommunityIcons name="pencil" size={18} color={P}/><Text style={[ss.footerBtnSecTxt,{color:P}]}>Edit Bid</Text></TouchableOpacity>
            <TouchableOpacity style={[ss.footerBtnPri,{backgroundColor:'#dc2626'}]} onPress={()=>exportBidPDF(b)}><MaterialCommunityIcons name="file-pdf-box" size={18} color="#fff"/><Text style={ss.footerBtnPriTxt}>Export PDF</Text></TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── QUOTATION DETAIL MODAL ───────────────────────────────────────────────────
  const renderQuotDetailModal = () => {
    if (!selectedQuot) return null;
    const q=selectedQuot;
    const sub=q.items?.reduce((a,i)=>a+(i.total||0),0)||0;
    const disc=q.discountType==='percentage'?sub*(q.discountValue||0)/100:(q.discountValue||0);
    const tax=(sub-disc)*(q.vatRate||20)/100;
    const total=sub-disc+tax;
    return (
      <Modal visible={showQuotDetail} animationType="slide" onRequestClose={()=>setShowQuotDetail(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          <View style={ss.modalHdr}>
            <TouchableOpacity onPress={()=>setShowQuotDetail(false)}><MaterialCommunityIcons name="arrow-left" size={24} color="#fff"/></TouchableOpacity>
            <Text style={ss.modalTitle}>{q.quotationNumber}</Text>
            <TouchableOpacity onPress={()=>exportQuotPDF(q)}><MaterialCommunityIcons name="file-pdf-box" size={24} color="#fff"/></TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>
            <View style={{backgroundColor:P,borderRadius:16,padding:20,marginBottom:16}}>
              <Text style={{color:'#fff',fontSize:17,fontWeight:'700',marginBottom:4}}>{q.title}</Text>
              <Text style={{color:'rgba(255,255,255,0.85)',fontSize:13}}>{q.clientName} · {q.clientCompany}</Text>
              <View style={{flexDirection:'row',gap:12,marginTop:12}}>
                <View style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:10,padding:10,flex:1,alignItems:'center'}}><Text style={{color:'#fff',fontSize:11,opacity:0.8}}>TOTAL</Text><Text style={{color:'#fff',fontSize:20,fontWeight:'700'}}>{fmtCur(total,q.currency)}</Text></View>
                <View style={{backgroundColor:'rgba(255,255,255,0.2)',borderRadius:10,padding:10,flex:1,alignItems:'center'}}><Text style={{color:'#fff',fontSize:11,opacity:0.8}}>STATUS</Text><Text style={{color:'#fff',fontSize:14,fontWeight:'700'}}>{(q.status||'').toUpperCase()}</Text></View>
              </View>
            </View>
            <View style={ss.grid2}>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Valid Until</Text><Text style={ss.detailVal}>{fmtDate(q.validUntil)}</Text></View>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Payment Terms</Text><Text style={ss.detailVal}>{q.paymentTerms||'—'}</Text></View>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Client Email</Text><Text style={ss.detailVal}>{q.clientEmail||'—'}</Text></View>
              <View style={ss.detailBox}><Text style={ss.detailLbl}>Client Phone</Text><Text style={ss.detailVal}>{q.clientPhone||'—'}</Text></View>
            </View>
            <View style={ss.descBox}><Text style={ss.descLbl}>Line Items</Text>
              {q.items?.map((it,i)=>(
                <View key={i} style={ss.lineItemRow}>
                  <View style={{flex:1}}><Text style={ss.liDesc}>{it.description}</Text><Text style={ss.liMeta}>{it.quantity} {it.unit} × {fmtCur(it.unitPrice,q.currency)}</Text></View>
                  <Text style={ss.liTotal}>{fmtCur(it.total,q.currency)}</Text>
                </View>
              ))}
              <View style={ss.totalSection}>
                <View style={ss.totRow}><Text style={ss.totLbl}>Subtotal</Text><Text style={ss.totVal}>{fmtCur(sub,q.currency)}</Text></View>
                {disc>0&&<View style={ss.totRow}><Text style={ss.totLbl}>Discount</Text><Text style={[ss.totVal,{color:'#059669'}]}>-{fmtCur(disc,q.currency)}</Text></View>}
                <View style={ss.totRow}><Text style={ss.totLbl}>VAT ({q.vatRate||20}%)</Text><Text style={ss.totVal}>{fmtCur(tax,q.currency)}</Text></View>
                <View style={[ss.totRow,{borderTopWidth:2,borderTopColor:P,paddingTop:8,marginTop:4}]}><Text style={[ss.totLbl,{fontSize:15,fontWeight:'700',color:P}]}>TOTAL</Text><Text style={[ss.totVal,{fontSize:15,fontWeight:'700',color:P}]}>{fmtCur(total,q.currency)}</Text></View>
              </View>
            </View>
            {q.notes&&<View style={ss.descBox}><Text style={ss.descLbl}>Notes</Text><Text style={ss.descTxt}>{q.notes}</Text></View>}
            {q.termsConditions&&<View style={ss.descBox}><Text style={ss.descLbl}>Terms & Conditions</Text><Text style={[ss.descTxt,{fontSize:12,color:'#475569'}]}>{q.termsConditions}</Text></View>}
          </ScrollView>
          <View style={ss.modalFooter}>
            <TouchableOpacity style={ss.footerBtnSec} onPress={()=>{setShowQuotDetail(false);setTimeout(()=>{setEditingQuot(q);setShowQuotForm(true);},300);}}><MaterialCommunityIcons name="pencil" size={18} color={P}/><Text style={[ss.footerBtnSecTxt,{color:P}]}>Edit</Text></TouchableOpacity>
            <TouchableOpacity style={[ss.footerBtnPri,{backgroundColor:'#dc2626'}]} onPress={()=>exportQuotPDF(q)}><MaterialCommunityIcons name="file-pdf-box" size={18} color="#fff"/><Text style={ss.footerBtnPriTxt}>Export PDF</Text></TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── MY TENDER FORM MODAL ─────────────────────────────────────────────────────
  const MyTenderFormModal = () => {
    const blank={title:'',description:'',category:'engineering',location:'',budgetMin:'',budgetMax:'',currency:'GBP',deadline:'',duration:'',contactEmail:'',contactPhone:'',tags:'',bond:false,bondAmount:'',requirements:[]};
    const [form,setForm]=useState(editingMyTender||blank);
    const [newReq,setNewReq]=useState('');
    const set=(k,v)=>setForm(f=>({...f,[k]:v}));
    return (
      <Modal visible={showMyTenderForm} animationType="slide" onRequestClose={()=>setShowMyTenderForm(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          <View style={ss.modalHdr}>
            <TouchableOpacity onPress={()=>setShowMyTenderForm(false)}><MaterialCommunityIcons name="close" size={24} color="#fff"/></TouchableOpacity>
            <Text style={ss.modalTitle}>{editingMyTender?'Edit Tender':'Post Tender'}</Text>
            <View style={{width:24}}/>
          </View>
          <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>
              <SectionHdr title="Tender Details"/>
              <Text style={ss.lbl}>Title *</Text>
              <TextInput style={ss.inp} value={form.title} onChangeText={v=>set('title',v)} placeholder="e.g. Civil Engineering Consultancy" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Description</Text>
              <TextInput style={[ss.inp,{height:90,textAlignVertical:'top'}]} value={form.description} onChangeText={v=>set('description',v)} multiline placeholder="Describe the work..." placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
                <View style={{flexDirection:'row',gap:8}}>
                  {CATEGORIES.filter(c=>c.id!=='all').map(c=>(
                    <TouchableOpacity key={c.id} style={[ss.subTab,form.category===c.id&&ss.subTabActive]} onPress={()=>set('category',c.id)}>
                      <Text style={[ss.subTabTxt,form.category===c.id&&ss.subTabTxtActive]}>{c.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
              <Text style={ss.lbl}>Location</Text>
              <TextInput style={ss.inp} value={form.location} onChangeText={v=>set('location',v)} placeholder="e.g. London, UK" placeholderTextColor="#94a3b8"/>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Min (£)</Text><TextInput style={ss.inp} value={form.budgetMin} onChangeText={v=>set('budgetMin',v)} keyboardType="numeric" placeholderTextColor="#94a3b8" placeholder="0"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Max (£)</Text><TextInput style={ss.inp} value={form.budgetMax} onChangeText={v=>set('budgetMax',v)} keyboardType="numeric" placeholderTextColor="#94a3b8" placeholder="0"/></View>
              </View>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Deadline (YYYY-MM-DD)</Text><TextInput style={ss.inp} value={form.deadline} onChangeText={v=>set('deadline',v)} placeholder="2025-12-31" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Duration</Text><TextInput style={ss.inp} value={form.duration} onChangeText={v=>set('duration',v)} placeholder="e.g. 6 months" placeholderTextColor="#94a3b8"/></View>
              </View>
              <Text style={ss.lbl}>Contact Email</Text>
              <TextInput style={ss.inp} value={form.contactEmail} onChangeText={v=>set('contactEmail',v)} keyboardType="email-address" placeholder="tender@yourcompany.co.uk" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Contact Phone</Text>
              <TextInput style={ss.inp} value={form.contactPhone} onChangeText={v=>set('contactPhone',v)} keyboardType="phone-pad" placeholder="+44 20 0000 0000" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Tags (comma separated)</Text>
              <TextInput style={ss.inp} value={form.tags} onChangeText={v=>set('tags',v)} placeholder="urgent, public-sector, heritage" placeholderTextColor="#94a3b8"/>
              <View style={[ss.switchRow,{marginBottom:12}]}>
                <Text style={ss.lbl}>Performance Bond Required</Text>
                <Switch value={form.bond} onValueChange={v=>set('bond',v)} trackColor={{true:P}} thumbColor={form.bond?P2:'#cbd5e1'}/>
              </View>
              {form.bond&&<><Text style={ss.lbl}>Bond Amount (£)</Text><TextInput style={ss.inp} value={form.bondAmount} onChangeText={v=>set('bondAmount',v)} keyboardType="numeric" placeholder="10000" placeholderTextColor="#94a3b8"/></>}
              <SectionHdr title="Requirements" count={form.requirements?.length} onAdd={()=>{ if(newReq.trim()){set('requirements',[...(form.requirements||[]),newReq.trim()]);setNewReq('');} }} addLabel="Add"/>
              <TextInput style={[ss.inp,{marginBottom:8}]} value={newReq} onChangeText={setNewReq} placeholder="Add a requirement..." placeholderTextColor="#94a3b8" onSubmitEditing={()=>{if(newReq.trim()){set('requirements',[...(form.requirements||[]),newReq.trim()]);setNewReq('');}}}/>
              {form.requirements?.map((r,i)=>(
                <View key={i} style={ss.listItemRow}>
                  <Text style={{flex:1,color:'#1e293b',fontSize:14}}>{r}</Text>
                  <TouchableOpacity onPress={()=>set('requirements',form.requirements.filter((_,j)=>j!==i))}><MaterialCommunityIcons name="close" size={18} color="#dc2626"/></TouchableOpacity>
                </View>
              ))}
            </ScrollView>
          </KeyboardAvoidingView>
          <View style={ss.modalFooter}>
            <TouchableOpacity style={ss.footerBtnSec} onPress={()=>{const d={...form,tags:typeof form.tags==='string'?form.tags.split(',').map(s=>s.trim()).filter(Boolean):form.tags};saveMyTender(d,false);setShowMyTenderForm(false);}}><Text style={[ss.footerBtnSecTxt,{color:P}]}>Save Draft</Text></TouchableOpacity>
            <TouchableOpacity style={ss.footerBtnPri} onPress={()=>{const d={...form,tags:typeof form.tags==='string'?form.tags.split(',').map(s=>s.trim()).filter(Boolean):form.tags};saveMyTender(d,true);setShowMyTenderForm(false);}}><MaterialCommunityIcons name="publish" size={18} color="#fff"/><Text style={ss.footerBtnPriTxt}>Publish Tender</Text></TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── BID FORM MODAL ───────────────────────────────────────────────────────────
  const BidFormModal = () => {
    const blank={tenderTitle:'',organisation:'',amount:'',currency:'GBP',timeline:'',validityDays:'60',proposal:'',paymentTerms:'30 days net',bondAmount:'',deliverables:[],team:[],certifications:[],previousExperience:'',additionalNotes:'',attachments:[],insuranceDetails:'',costSheet:null};
    const [form,setForm]=useState(editingBid||blank);
    const [newDel,setNewDel]=useState(''); const [newCert,setNewCert]=useState('');
    const [newTM,setNewTM]=useState({name:'',role:'',experience:''});
    const set=(k,v)=>setForm(f=>({...f,[k]:v}));
    return (
      <Modal visible={showBidForm} animationType="slide" onRequestClose={()=>setShowBidForm(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          <View style={ss.modalHdr}>
            <TouchableOpacity onPress={()=>setShowBidForm(false)}><MaterialCommunityIcons name="close" size={24} color="#fff"/></TouchableOpacity>
            <Text style={ss.modalTitle}>{editingBid?.id?'Edit Bid':'New Bid'}</Text>
            <View style={{width:24}}/>
          </View>
          <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>
              <SectionHdr title="Tender Reference"/>
              <Text style={ss.lbl}>Tender Title *</Text>
              <TextInput style={ss.inp} value={form.tenderTitle} onChangeText={v=>set('tenderTitle',v)} placeholder="Tender title" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Organisation</Text>
              <TextInput style={ss.inp} value={form.organisation} onChangeText={v=>set('organisation',v)} placeholder="Issuing organisation" placeholderTextColor="#94a3b8"/>
              <SectionHdr title="Bid Financials"/>
              <Text style={ss.lbl}>Currency</Text>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:12}}>
                {CURRENCIES.map(c=><TouchableOpacity key={c} style={[ss.chipBtn,form.currency===c&&ss.chipBtnActive]} onPress={()=>set('currency',c)}><Text style={[ss.chipTxt,form.currency===c&&ss.chipTxtActive]}>{c}</Text></TouchableOpacity>)}
              </View>
              <Text style={ss.lbl}>Bid Amount *</Text>
              <TextInput style={ss.inp} value={String(form.amount||'')} onChangeText={v=>set('amount',v)} keyboardType="numeric" placeholder="0" placeholderTextColor="#94a3b8"/>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Timeline</Text><TextInput style={ss.inp} value={form.timeline} onChangeText={v=>set('timeline',v)} placeholder="e.g. 6 months" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Validity (days)</Text><TextInput style={ss.inp} value={String(form.validityDays||'')} onChangeText={v=>set('validityDays',v)} keyboardType="numeric" placeholder="60" placeholderTextColor="#94a3b8"/></View>
              </View>
              <Text style={ss.lbl}>Bond Amount</Text>
              <TextInput style={ss.inp} value={String(form.bondAmount||'')} onChangeText={v=>set('bondAmount',v)} keyboardType="numeric" placeholder="0" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Insurance Details</Text>
              <TextInput style={ss.inp} value={form.insuranceDetails} onChangeText={v=>set('insuranceDetails',v)} placeholder="PII, public liability etc." placeholderTextColor="#94a3b8"/>

              {/* ── Cost Spreadsheet ── */}
              <SectionHdr title="Cost Breakdown Spreadsheet"/>
              <Text style={{fontSize:12,color:'#64748b',marginBottom:12,lineHeight:17}}>
                Build your cost from scratch. The total will auto-populate the Bid Amount above including your margin.
              </Text>
              <CostSheetCalculator
                value={form.costSheet}
                currency={form.currency}
                onChange={cs => {
                  set('costSheet', cs);
                  // Auto-fill bid amount from cost sheet total
                  if (cs.bidTotal > 0) set('amount', cs.bidTotal.toFixed(2));
                }}
              />
              <Text style={ss.lbl}>Executive Proposal / Summary</Text>
              <TextInput style={[ss.inp,{height:120,textAlignVertical:'top'}]} value={form.proposal} onChangeText={v=>set('proposal',v)} multiline placeholder="Describe your approach, methodology and why you are the best choice..." placeholderTextColor="#94a3b8"/>
              <SectionHdr title="Payment Terms"/>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:12}}>
                {PAY_TERMS.map(t=><TouchableOpacity key={t} style={[ss.chipBtn,form.paymentTerms===t&&ss.chipBtnActive]} onPress={()=>set('paymentTerms',t)}><Text style={[ss.chipTxt,form.paymentTerms===t&&ss.chipTxtActive]}>{t}</Text></TouchableOpacity>)}
              </View>
              <Text style={ss.lbl}>Custom Payment Terms</Text>
              <TextInput style={ss.inp} value={PAY_TERMS.includes(form.paymentTerms)?'':form.paymentTerms} onChangeText={v=>set('paymentTerms',v)} placeholder="Custom terms..." placeholderTextColor="#94a3b8"/>
              <SectionHdr title="Deliverables" count={form.deliverables?.length} onAdd={()=>{if(newDel.trim()){set('deliverables',[...(form.deliverables||[]),newDel.trim()]);setNewDel('');}}} addLabel="Add"/>
              <TextInput style={[ss.inp,{marginBottom:8}]} value={newDel} onChangeText={setNewDel} placeholder="Add deliverable..." placeholderTextColor="#94a3b8" onSubmitEditing={()=>{if(newDel.trim()){set('deliverables',[...(form.deliverables||[]),newDel.trim()]);setNewDel('');}}}/>
              {form.deliverables?.map((d,i)=>(
                <View key={i} style={ss.listItemRow}><Text style={{flex:1,color:'#1e293b',fontSize:14}}>{d}</Text><TouchableOpacity onPress={()=>set('deliverables',form.deliverables.filter((_,j)=>j!==i))}><MaterialCommunityIcons name="close" size={18} color="#dc2626"/></TouchableOpacity></View>
              ))}
              <SectionHdr title="Project Team" count={form.team?.length} onAdd={()=>{if(newTM.name.trim()){set('team',[...(form.team||[]),{...newTM}]);setNewTM({name:'',role:'',experience:''});}}} addLabel="Add"/>
              <View style={{backgroundColor:'#f1f5f9',borderRadius:10,padding:12,marginBottom:8}}>
                <TextInput style={[ss.inp,{marginBottom:6}]} value={newTM.name} onChangeText={v=>setNewTM(m=>({...m,name:v}))} placeholder="Name" placeholderTextColor="#94a3b8"/>
                <TextInput style={[ss.inp,{marginBottom:6}]} value={newTM.role} onChangeText={v=>setNewTM(m=>({...m,role:v}))} placeholder="Role" placeholderTextColor="#94a3b8"/>
                <TextInput style={ss.inp} value={newTM.experience} onChangeText={v=>setNewTM(m=>({...m,experience:v}))} placeholder="Experience" placeholderTextColor="#94a3b8"/>
              </View>
              {form.team?.map((m,i)=>(
                <View key={i} style={ss.listItemRow}><View style={ss.teamAvatar}><Text style={ss.teamAvatarTxt}>{(m.name||'?')[0]}</Text></View><View style={{flex:1,marginLeft:8}}><Text style={ss.teamName}>{m.name}</Text><Text style={ss.teamRole}>{m.role} · {m.experience}</Text></View><TouchableOpacity onPress={()=>set('team',form.team.filter((_,j)=>j!==i))}><MaterialCommunityIcons name="close" size={18} color="#dc2626"/></TouchableOpacity></View>
              ))}
              <SectionHdr title="Certifications" count={form.certifications?.length} onAdd={()=>{if(newCert.trim()){set('certifications',[...(form.certifications||[]),newCert.trim()]);setNewCert('');}}} addLabel="Add"/>
              <TextInput style={[ss.inp,{marginBottom:8}]} value={newCert} onChangeText={setNewCert} placeholder="e.g. ISO 9001, NICEIC..." placeholderTextColor="#94a3b8" onSubmitEditing={()=>{if(newCert.trim()){set('certifications',[...(form.certifications||[]),newCert.trim()]);setNewCert('');}}}/>
              {form.certifications?.map((c,i)=>(
                <View key={i} style={ss.listItemRow}><Text style={{flex:1,color:'#1e293b',fontSize:14}}>{c}</Text><TouchableOpacity onPress={()=>set('certifications',form.certifications.filter((_,j)=>j!==i))}><MaterialCommunityIcons name="close" size={18} color="#dc2626"/></TouchableOpacity></View>
              ))}
              <SectionHdr title="Experience & Notes"/>
              <Text style={ss.lbl}>Previous Experience</Text>
              <TextInput style={[ss.inp,{height:80,textAlignVertical:'top'}]} value={form.previousExperience} onChangeText={v=>set('previousExperience',v)} multiline placeholder="Describe relevant previous projects..." placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Additional Notes</Text>
              <TextInput style={[ss.inp,{height:60,textAlignVertical:'top'}]} value={form.additionalNotes} onChangeText={v=>set('additionalNotes',v)} multiline placeholder="Any additional information..." placeholderTextColor="#94a3b8"/>
            </ScrollView>
          </KeyboardAvoidingView>
          <View style={ss.modalFooter}>
            <TouchableOpacity style={ss.footerBtnSec} onPress={()=>{saveBid({...form,id:editingBid?.id},false);setShowBidForm(false);}}><Text style={[ss.footerBtnSecTxt,{color:P}]}>Save Draft</Text></TouchableOpacity>
            <TouchableOpacity style={ss.footerBtnPri} onPress={()=>{saveBid({...form,id:editingBid?.id},true);setShowBidForm(false);}}><MaterialCommunityIcons name="send" size={18} color="#fff"/><Text style={ss.footerBtnPriTxt}>Submit Bid</Text></TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── QUOTATION FORM MODAL ─────────────────────────────────────────────────────
  const QuotFormModal = () => {
    const blank={clientName:'',clientCompany:'',clientEmail:'',clientPhone:'',clientAddress:'',title:'',description:'',validUntil:'',currency:'GBP',items:[],vatRate:20,discountType:'percentage',discountValue:0,paymentTerms:'30 days net',notes:'',termsConditions:''};
    const [form,setForm]=useState(editingQuot||blank);
    const [newItem,setNewItem]=useState({description:'',quantity:'1',unit:'',unitPrice:'0'});
    const set=(k,v)=>setForm(f=>({...f,[k]:v}));
    const sub=form.items?.reduce((a,i)=>a+(i.total||0),0)||0;
    const disc=form.discountType==='percentage'?sub*(form.discountValue||0)/100:(form.discountValue||0);
    const tax=(sub-disc)*(form.vatRate||20)/100;
    const total=sub-disc+tax;
    const addItem=()=>{
      if(!newItem.description.trim()) return;
      const qty=Number(newItem.quantity)||1; const up=Number(newItem.unitPrice)||0;
      const it={id:uid(),description:newItem.description,quantity:qty,unit:newItem.unit,unitPrice:up,total:qty*up};
      set('items',[...(form.items||[]),it]);
      setNewItem({description:'',quantity:'1',unit:'',unitPrice:'0'});
    };
    const applyTemplate=(t)=>{
      set('items',t.items.map(i=>({...i,id:uid()})));
      set('paymentTerms',t.paymentTerms);
      set('notes',t.notes);
    };
    return (
      <Modal visible={showQuotForm} animationType="slide" onRequestClose={()=>setShowQuotForm(false)}>
        <SafeAreaView style={{flex:1,backgroundColor:'#f8fafc'}}>
          <View style={ss.modalHdr}>
            <TouchableOpacity onPress={()=>setShowQuotForm(false)}><MaterialCommunityIcons name="close" size={24} color="#fff"/></TouchableOpacity>
            <Text style={ss.modalTitle}>{editingQuot?.id?'Edit Quotation':'New Quotation'}</Text>
            <View style={{width:24}}/>
          </View>
          <KeyboardAvoidingView style={{flex:1}} behavior={Platform.OS==='ios'?'padding':'height'}>
            <ScrollView contentContainerStyle={{padding:16,paddingBottom:40}}>
              <SectionHdr title="Templates"/>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
                <View style={{flexDirection:'row',gap:8}}>
                  {QUOT_TEMPLATES.map(t=>(
                    <TouchableOpacity key={t.id} style={[ss.subTab,{backgroundColor:'#e0f2fe',borderColor:P2}]} onPress={()=>applyTemplate(t)}>
                      <MaterialCommunityIcons name="file-document-outline" size={14} color={P2}/>
                      <Text style={[ss.subTabTxt,{color:P2,marginLeft:4}]}>{t.name}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
              <SectionHdr title="Client Details"/>
              <Text style={ss.lbl}>Client Name *</Text>
              <TextInput style={ss.inp} value={form.clientName} onChangeText={v=>set('clientName',v)} placeholder="Contact name" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Company</Text>
              <TextInput style={ss.inp} value={form.clientCompany} onChangeText={v=>set('clientCompany',v)} placeholder="Company name" placeholderTextColor="#94a3b8"/>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Email</Text><TextInput style={ss.inp} value={form.clientEmail} onChangeText={v=>set('clientEmail',v)} keyboardType="email-address" placeholder="email@client.co.uk" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Phone</Text><TextInput style={ss.inp} value={form.clientPhone} onChangeText={v=>set('clientPhone',v)} keyboardType="phone-pad" placeholder="+44" placeholderTextColor="#94a3b8"/></View>
              </View>
              <Text style={ss.lbl}>Client Address</Text>
              <TextInput style={ss.inp} value={form.clientAddress} onChangeText={v=>set('clientAddress',v)} placeholder="123 High Street, London EC1A 1BB" placeholderTextColor="#94a3b8"/>
              <SectionHdr title="Quotation Details"/>
              <Text style={ss.lbl}>Title *</Text>
              <TextInput style={ss.inp} value={form.title} onChangeText={v=>set('title',v)} placeholder="e.g. Structural Engineering Consultancy" placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Description</Text>
              <TextInput style={[ss.inp,{height:70,textAlignVertical:'top'}]} value={form.description} onChangeText={v=>set('description',v)} multiline placeholder="Brief scope description..." placeholderTextColor="#94a3b8"/>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Valid Until</Text><TextInput style={ss.inp} value={form.validUntil} onChangeText={v=>set('validUntil',v)} placeholder="YYYY-MM-DD" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Currency</Text>
                  <View style={{flexDirection:'row',flexWrap:'wrap',gap:4}}>
                    {CURRENCIES.map(c=><TouchableOpacity key={c} style={[ss.chipBtn,{paddingVertical:6,paddingHorizontal:10},form.currency===c&&ss.chipBtnActive]} onPress={()=>set('currency',c)}><Text style={[ss.chipTxt,form.currency===c&&ss.chipTxtActive]}>{c}</Text></TouchableOpacity>)}
                  </View>
                </View>
              </View>
              <SectionHdr title="Line Items" count={form.items?.length} onAdd={addItem} addLabel="Add"/>
              <View style={{backgroundColor:'#f1f5f9',borderRadius:10,padding:12,marginBottom:8,gap:6}}>
                <TextInput style={ss.inp} value={newItem.description} onChangeText={v=>setNewItem(n=>({...n,description:v}))} placeholder="Description" placeholderTextColor="#94a3b8"/>
                <View style={ss.row2}>
                  <View style={{flex:1}}><TextInput style={ss.inp} value={newItem.quantity} onChangeText={v=>setNewItem(n=>({...n,quantity:v}))} keyboardType="numeric" placeholder="Qty" placeholderTextColor="#94a3b8"/></View>
                  <View style={{flex:1}}><TextInput style={ss.inp} value={newItem.unit} onChangeText={v=>setNewItem(n=>({...n,unit:v}))} placeholder="Unit" placeholderTextColor="#94a3b8"/></View>
                  <View style={{flex:1}}><TextInput style={ss.inp} value={newItem.unitPrice} onChangeText={v=>setNewItem(n=>({...n,unitPrice:v}))} keyboardType="numeric" placeholder="Price" placeholderTextColor="#94a3b8"/></View>
                </View>
                <Text style={{color:P,fontSize:12,textAlign:'right'}}>Subtotal: {fmtCur((Number(newItem.quantity)||1)*(Number(newItem.unitPrice)||0),form.currency)}</Text>
              </View>
              {form.items?.map((it,i)=>(
                <View key={it.id||i} style={ss.lineItemRow}>
                  <View style={{flex:1}}><Text style={ss.liDesc}>{it.description}</Text><Text style={ss.liMeta}>{it.quantity} {it.unit} × {fmtCur(it.unitPrice,form.currency)}</Text></View>
                  <Text style={ss.liTotal}>{fmtCur(it.total,form.currency)}</Text>
                  <TouchableOpacity onPress={()=>set('items',form.items.filter((_,j)=>j!==i))} style={{marginLeft:8}}><MaterialCommunityIcons name="close" size={18} color="#dc2626"/></TouchableOpacity>
                </View>
              ))}
              {form.items?.length>0&&(
                <View style={ss.totalSection}>
                  <View style={ss.totRow}><Text style={ss.totLbl}>Subtotal</Text><Text style={ss.totVal}>{fmtCur(sub,form.currency)}</Text></View>
                  <View style={ss.totRow}><Text style={ss.totLbl}>VAT ({form.vatRate}%)</Text>
                    <TextInput style={[ss.inp,{width:60,textAlign:'center',marginBottom:0}]} value={String(form.vatRate||20)} onChangeText={v=>set('vatRate',Number(v)||20)} keyboardType="numeric"/>
                  </View>
                  <View style={ss.totRow}>
                    <View style={{flexDirection:'row',gap:8,alignItems:'center',flex:1}}>
                      <Text style={ss.totLbl}>Discount</Text>
                      <TouchableOpacity style={[ss.chipBtn,form.discountType==='percentage'&&ss.chipBtnActive]} onPress={()=>set('discountType','percentage')}><Text style={[ss.chipTxt,form.discountType==='percentage'&&ss.chipTxtActive]}>%</Text></TouchableOpacity>
                      <TouchableOpacity style={[ss.chipBtn,form.discountType==='fixed'&&ss.chipBtnActive]} onPress={()=>set('discountType','fixed')}><Text style={[ss.chipTxt,form.discountType==='fixed'&&ss.chipTxtActive]}>Fixed</Text></TouchableOpacity>
                    </View>
                    <TextInput style={[ss.inp,{width:80,textAlign:'right',marginBottom:0}]} value={String(form.discountValue||0)} onChangeText={v=>set('discountValue',Number(v)||0)} keyboardType="numeric"/>
                  </View>
                  <View style={[ss.totRow,{borderTopWidth:2,borderTopColor:P,paddingTop:8,marginTop:4}]}><Text style={[ss.totLbl,{fontWeight:'700',color:P,fontSize:15}]}>TOTAL</Text><Text style={[ss.totVal,{fontWeight:'700',color:P,fontSize:15}]}>{fmtCur(total,form.currency)}</Text></View>
                </View>
              )}
              <SectionHdr title="Payment Terms"/>
              <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:8}}>
                {PAY_TERMS.map(t=><TouchableOpacity key={t} style={[ss.chipBtn,form.paymentTerms===t&&ss.chipBtnActive]} onPress={()=>set('paymentTerms',t)}><Text style={[ss.chipTxt,form.paymentTerms===t&&ss.chipTxtActive]}>{t}</Text></TouchableOpacity>)}
              </View>
              <TextInput style={ss.inp} value={PAY_TERMS.includes(form.paymentTerms)?'':form.paymentTerms} onChangeText={v=>set('paymentTerms',v)} placeholder="Custom terms..." placeholderTextColor="#94a3b8"/>
              <SectionHdr title="Notes & T&Cs"/>
              <Text style={ss.lbl}>Notes</Text>
              <TextInput style={[ss.inp,{height:70,textAlignVertical:'top'}]} value={form.notes} onChangeText={v=>set('notes',v)} multiline placeholder="Any notes for the client..." placeholderTextColor="#94a3b8"/>
              <Text style={ss.lbl}>Terms & Conditions</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:8}}>
                <View style={{flexDirection:'row',gap:8}}>
                  {TC_TEMPLATES.map(tc=><TouchableOpacity key={tc.id} style={[ss.subTab,{backgroundColor:'#fef3c7',borderColor:'#d97706'}]} onPress={()=>set('termsConditions',tc.text)}><Text style={[ss.subTabTxt,{color:'#d97706'}]}>{tc.name}</Text></TouchableOpacity>)}
                </View>
              </ScrollView>
              <TextInput style={[ss.inp,{height:100,textAlignVertical:'top'}]} value={form.termsConditions} onChangeText={v=>set('termsConditions',v)} multiline placeholder="Terms and conditions..." placeholderTextColor="#94a3b8"/>
            </ScrollView>
          </KeyboardAvoidingView>
          <View style={ss.modalFooter}>
            <TouchableOpacity style={ss.footerBtnSec} onPress={()=>{saveQuotation({...form,id:editingQuot?.id},false);setShowQuotForm(false);}}><Text style={[ss.footerBtnSecTxt,{color:P}]}>Save Draft</Text></TouchableOpacity>
            <TouchableOpacity style={ss.footerBtnPri} onPress={()=>{saveQuotation({...form,id:editingQuot?.id},true);setShowQuotForm(false);}}><MaterialCommunityIcons name="send" size={18} color="#fff"/><Text style={ss.footerBtnPriTxt}>Send Quotation</Text></TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── FILTER / SORT MODALS ─────────────────────────────────────────────────────
  const TenderFilterModal = () => {
    const [f,setF]=useState(tenderFilter);
    return (
      <Modal visible={showTenderFilter} animationType="slide" transparent onRequestClose={()=>setShowTenderFilter(false)}>
        <View style={ss.sheetOverlay}>
          <View style={ss.sheet}>
            <View style={ss.sheetHdr}><Text style={ss.sheetTitle}>Filter Tenders</Text><TouchableOpacity onPress={()=>setShowTenderFilter(false)}><MaterialCommunityIcons name="close" size={22} color="#64748B"/></TouchableOpacity></View>
            <ScrollView style={{maxHeight:420}}>
              <Text style={ss.lbl}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
                <View style={{flexDirection:'row',gap:8}}>
                  {CATEGORIES.map(c=><TouchableOpacity key={c.id} style={[ss.chipBtn,f.category===c.id&&ss.chipBtnActive]} onPress={()=>setF(x=>({...x,category:c.id}))}><Text style={[ss.chipTxt,f.category===c.id&&ss.chipTxtActive]}>{c.label}</Text></TouchableOpacity>)}
                </View>
              </ScrollView>
              <View style={ss.row2}>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Min (£)</Text><TextInput style={ss.inp} value={f.budgetMin} onChangeText={v=>setF(x=>({...x,budgetMin:v}))} keyboardType="numeric" placeholder="0" placeholderTextColor="#94a3b8"/></View>
                <View style={{flex:1}}><Text style={ss.lbl}>Budget Max (£)</Text><TextInput style={ss.inp} value={f.budgetMax} onChangeText={v=>setF(x=>({...x,budgetMax:v}))} keyboardType="numeric" placeholder="Any" placeholderTextColor="#94a3b8"/></View>
              </View>
              <Text style={ss.lbl}>Location</Text>
              <TextInput style={ss.inp} value={f.location} onChangeText={v=>setF(x=>({...x,location:v}))} placeholder="e.g. London" placeholderTextColor="#94a3b8"/>
              <View style={ss.switchRow}><Text style={ss.lbl}>Bond Required Only</Text><Switch value={f.bond} onValueChange={v=>setF(x=>({...x,bond:v}))} trackColor={{true:P}} thumbColor={f.bond?P2:'#cbd5e1'}/></View>
            </ScrollView>
            <View style={ss.row2}>
              <TouchableOpacity style={[ss.footerBtnSec,{flex:1}]} onPress={()=>{setTenderFilter({category:'all',budgetMin:'',budgetMax:'',location:'',bond:false});setShowTenderFilter(false);}}><Text style={[ss.footerBtnSecTxt,{color:'#64748B'}]}>Reset</Text></TouchableOpacity>
              <TouchableOpacity style={[ss.footerBtnPri,{flex:1}]} onPress={()=>{setTenderFilter(f);setShowTenderFilter(false);}}><Text style={ss.footerBtnPriTxt}>Apply</Text></TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const TenderSortModal = () => (
    <Modal visible={showTenderSort} animationType="slide" transparent onRequestClose={()=>setShowTenderSort(false)}>
      <View style={ss.sheetOverlay}>
        <View style={ss.sheet}>
          <View style={ss.sheetHdr}><Text style={ss.sheetTitle}>Sort Tenders</Text><TouchableOpacity onPress={()=>setShowTenderSort(false)}><MaterialCommunityIcons name="close" size={22} color="#64748B"/></TouchableOpacity></View>
          {[['deadline','Deadline (Soonest First)'],['budget_h','Budget (Highest First)'],['budget_l','Budget (Lowest First)'],['newest','Newest First'],['applicants','Most Applicants']].map(([k,l])=>(
            <TouchableOpacity key={k} style={ss.sortRow} onPress={()=>{setTenderSort(k);setShowTenderSort(false);}}>
              <Text style={[ss.sortTxt,tenderSort===k&&{color:P,fontWeight:'700'}]}>{l}</Text>
              {tenderSort===k&&<MaterialCommunityIcons name="check" size={18} color={P}/>}
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </Modal>
  );

  // ─── MAIN RENDER ─────────────────────────────────────────────────────────────
  if (loading) return (
    <SafeAreaView style={ss.root}>
      <View style={ss.loadingBox}><ActivityIndicator size="large" color={P}/><Text style={ss.loadingTxt}>Loading TBQ Module...</Text></View>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={ss.root} edges={['top']}>
      <View style={ss.header}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={ss.backBtn}><MaterialCommunityIcons name="arrow-left" size={22} color={P}/></TouchableOpacity>
        <View style={{flex:1,flexDirection:'row',alignItems:'center',gap:8}}>
          {companyLogo
            ? <Image source={{uri:companyLogo}} style={ss.headerLogo}/>
            : <MaterialCommunityIcons name="file-document-outline" size={24} color={P}/>
          }
          <View>
            <Text style={ss.headerTitle}>Tenders, Bids & Quotations</Text>
            <Text style={ss.headerSub}>Manage your pipeline</Text>
          </View>
        </View>
        <TouchableOpacity onPress={onRefresh} style={ss.backBtn}><MaterialCommunityIcons name="refresh" size={22} color={P}/></TouchableOpacity>
      </View>
      {renderMainTabs()}
      <View style={{flex:1}}>
        {mainTab==='tenders'   && renderTendersTab()}
        {mainTab==='bids'      && renderBidsTab()}
        {mainTab==='quotations'&& renderQuotationsTab()}
        {mainTab==='uk_live'   && <UKTendersTab bizId={getBizId} />}
        {mainTab==='analytics' && renderAnalyticsTab()}
      </View>
      {renderTenderDetailModal()}
      {renderBidDetailModal()}
      {renderQuotDetailModal()}
      <MyTenderFormModal/>
      <BidFormModal/>
      <QuotFormModal/>
      <TenderFilterModal/>
      <TenderSortModal/>
    </SafeAreaView>
  );
}

// ─── STYLESHEET ───────────────────────────────────────────────────────────────
const ss = StyleSheet.create({

  // ╔══════════════════════════════════════════════════════╗
  // ║  FOUNDATION                                          ║
  // ╚══════════════════════════════════════════════════════╝
  root: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },

  // ── Loading
  loadingBox: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
  },
  loadingTxt: {
    color: '#64748b',
    fontSize: 15,
    fontWeight: '500',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  HEADER                                              ║
  // ╚══════════════════════════════════════════════════════╝
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    backgroundColor: P,
    gap: 12,
  },
  backBtn: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(255,255,255,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerLogo: {
    width: 34,
    height: 34,
    borderRadius: 10,
    resizeMode: 'cover',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: '800',
    color: '#fff',
    letterSpacing: -0.3,
  },
  headerSub: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.72)',
    marginTop: 1,
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  STATS BANNER                                        ║
  // ╚══════════════════════════════════════════════════════╝
  statsBanner: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e8edf2',
  },
  statCard: {
    backgroundColor: '#f0f5ff',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 6,
    width: 100,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    borderLeftWidth: 3,
  },
  statVal: {
    fontSize: 15,
    fontWeight: '800',
    color: '#1e293b',
  },
  statLbl: {
    fontSize: 9,
    color: '#64748b',
    textAlign: 'center',
    fontWeight: '600',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  NAVIGATION — MAIN TABS                              ║
  // ╚══════════════════════════════════════════════════════╝
  mainTabBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e8edf2',
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  mainTab: {
    flex: 1,
    paddingTop: 10,
    paddingBottom: 8,
    alignItems: 'center',
    gap: 3,
  },
  mainTabActive: {
    borderBottomWidth: 3,
    borderBottomColor: P,
  },
  mainTabTxt: {
    fontSize: 10,
    color: '#94a3b8',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  mainTabTxtActive: {
    color: P,
    fontWeight: '800',
  },

  // ── Sub Tabs
  subTabBar: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e8edf2',
    height: 52,
  },
  subTab: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 7,
    backgroundColor: '#f0f4f8',
    borderWidth: 1.5,
    borderColor: 'transparent',
  },
  subTabActive: {
    backgroundColor: '#fff',
    borderColor: P,
  },
  subTabTxt: {
    fontSize: 13,
    color: '#475569',
    fontWeight: '600',
  },
  subTabTxtActive: {
    color: P,
    fontWeight: '800',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  SEARCH & CONTROLS                                   ║
  // ╚══════════════════════════════════════════════════════╝
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 8,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    borderRadius: 12,
    paddingHorizontal: 12,
    height: 42,
    gap: 8,
    borderWidth: 1,
    borderColor: '#e8edf2',
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '500',
  },
  iconBtn: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: '#f0f4f8',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e8edf2',
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: P,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    gap: 5,
    shadowColor: P,
    shadowOpacity: 0.35,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
    elevation: 4,
  },
  addBtnTxt: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  CARDS                                               ║
  // ╚══════════════════════════════════════════════════════╝
  card: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 16,
    shadowColor: '#1e293b',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.08,
    shadowRadius: 10,
    elevation: 4,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#0f172a',
    lineHeight: 22,
  },
  cardSub: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
    fontWeight: '500',
  },
  catIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // ── Tags & Pills
  tagChip: {
    backgroundColor: '#eff6ff',
    borderRadius: 8,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: '#bfdbfe',
  },
  tagChipTxt: {
    fontSize: 10,
    color: '#3b82f6',
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  pill: {
    borderRadius: 20,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderWidth: 1.5,
  },
  pillTxt: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.4,
  },

  // ── Meta & Actions
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaTxt: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '500',
  },
  acBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 6,
    gap: 5,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  acTxt: {
    fontSize: 12,
    color: '#475569',
    fontWeight: '600',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  EMPTY STATE                                         ║
  // ╚══════════════════════════════════════════════════════╝
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 48,
    gap: 14,
  },
  emptyTxt: {
    color: '#94a3b8',
    fontSize: 15,
    textAlign: 'center',
    fontWeight: '500',
    lineHeight: 22,
  },
  emptyBtn: {
    backgroundColor: P,
    borderRadius: 14,
    paddingHorizontal: 24,
    paddingVertical: 12,
    shadowColor: P,
    shadowOpacity: 0.3,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 4 },
    elevation: 4,
  },
  emptyBtnTxt: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  ANALYTICS                                           ║
  // ╚══════════════════════════════════════════════════════╝
  secLabel: {
    fontSize: 11,
    fontWeight: '800',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },

  // ── KPI Grid
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  kpiCard: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 16,
    width: '47%',
    alignItems: 'center',
    gap: 6,
    shadowColor: '#1e293b',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07,
    shadowRadius: 8,
    elevation: 3,
  },
  kpiIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiVal: {
    fontSize: 20,
    fontWeight: '900',
    letterSpacing: -0.5,
  },
  kpiLbl: {
    fontSize: 11,
    color: '#94a3b8',
    textAlign: 'center',
    fontWeight: '600',
  },

  // ── Chart Card
  chartCard: {
    backgroundColor: '#fff',
    borderRadius: 18,
    padding: 18,
    shadowColor: '#1e293b',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07,
    shadowRadius: 8,
    elevation: 3,
  },
  chartTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#0f172a',
    marginBottom: 4,
  },
  barRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 5,
  },
  barLabel: {
    fontSize: 12,
    color: '#475569',
    textTransform: 'capitalize',
    flex: 1,
    fontWeight: '500',
  },
  barVal: {
    fontSize: 12,
    fontWeight: '800',
    minWidth: 28,
    textAlign: 'right',
  },
  barBg: {
    height: 10,
    backgroundColor: '#f0f4f8',
    borderRadius: 5,
    overflow: 'hidden',
  },
  barFill: {
    height: 10,
    borderRadius: 5,
  },

  // ── Urgent / Alerts
  urgentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fffbeb',
    borderRadius: 14,
    padding: 13,
    borderWidth: 1.5,
    borderColor: '#fcd34d',
    marginBottom: 8,
  },
  urgentTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1e293b',
  },
  urgentSub: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 1,
  },
  urgentBadge: {
    backgroundColor: '#fee2e2',
    borderWidth: 1.5,
    borderColor: '#ef4444',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  urgentBadgeTxt: {
    fontSize: 11,
    fontWeight: '800',
    color: '#dc2626',
  },

  // ── Activity Log
  actRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 12,
    marginBottom: 6,
    gap: 10,
  },
  actMsg: {
    fontSize: 13,
    color: '#1e293b',
    flex: 1,
    fontWeight: '500',
  },
  actDate: {
    fontSize: 11,
    color: '#94a3b8',
    marginTop: 2,
  },

  // ── Quick Actions
  qaGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  qaBtn: {
    backgroundColor: '#fff',
    borderRadius: 16,
    padding: 18,
    width: '47%',
    alignItems: 'center',
    gap: 8,
    shadowColor: '#1e293b',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.07,
    shadowRadius: 8,
    elevation: 3,
  },
  qaIcon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
  },
  qaTxt: {
    fontSize: 13,
    fontWeight: '700',
    textAlign: 'center',
    color: '#1e293b',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  MODALS                                              ║
  // ╚══════════════════════════════════════════════════════╝
  modalHdr: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 18,
    paddingVertical: 54,
    backgroundColor: P,
    gap: 12,
  },
  modalTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: '#fff',
    letterSpacing: -0.2,
  },
  modalFooter: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingVertical: 12,
    paddingBottom: Platform.OS === 'ios' ? 28 : 14,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eef2f7',
    gap: 8,
  },
  footerBtnPri: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: P,
    borderRadius: 12,
    paddingVertical: 13,
    gap: 6,
    shadowColor: P,
    shadowOpacity: 0.25,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
    elevation: 3,
  },
  footerBtnPriTxt: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  footerBtnSec: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f4f8',
    borderRadius: 12,
    paddingVertical: 13,
    gap: 5,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  footerBtnSecTxt: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  DETAIL VIEWS                                        ║
  // ╚══════════════════════════════════════════════════════╝
  grid2: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  detailBox: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 12,
    flex: 1,
    minWidth: '45%',
    borderWidth: 1,
    borderColor: '#eef2f7',
  },
  detailLbl: {
    fontSize: 10,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontWeight: '700',
  },
  detailVal: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0f172a',
    marginTop: 3,
  },
  descBox: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#eef2f7',
  },
  descLbl: {
    fontSize: 10,
    fontWeight: '700',
    color: P,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 7,
  },
  descTxt: {
    fontSize: 13,
    color: '#475569',
    lineHeight: 20,
  },
  heroBox: {
    marginBottom: 0,
  },
  alertBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fffbeb',
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#fcd34d',
  },

  // ── Requirements
  reqRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 7,
  },
  reqTxt: {
    fontSize: 13,
    color: '#1e293b',
    flex: 1,
    fontWeight: '500',
  },

  // ── Team
  teamRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 10,
  },
  teamAvatar: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: P,
    alignItems: 'center',
    justifyContent: 'center',
  },
  teamAvatarTxt: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '800',
  },
  teamName: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0f172a',
  },
  teamRole: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 1,
  },

  // ── Certifications
  certChip: {
    backgroundColor: '#eff6ff',
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: '#bfdbfe',
  },
  certChipTxt: {
    fontSize: 12,
    color: '#3b82f6',
    fontWeight: '700',
  },

  // ── Line Items & Totals
  lineItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 11,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  liDesc: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0f172a',
  },
  liMeta: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 2,
  },
  liTotal: {
    fontSize: 14,
    fontWeight: '800',
    color: P,
    minWidth: 80,
    textAlign: 'right',
  },
  totalSection: {
    backgroundColor: '#f8fafc',
    borderRadius: 14,
    padding: 14,
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#f0f4f8',
  },
  totRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
  },
  totLbl: {
    fontSize: 13,
    color: '#64748b',
    fontWeight: '500',
  },
  totVal: {
    fontSize: 13,
    fontWeight: '700',
    color: '#1e293b',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  FORMS                                               ║
  // ╚══════════════════════════════════════════════════════╝
  lbl: {
    fontSize: 12,
    fontWeight: '700',
    color: '#64748b',
    marginBottom: 5,
    marginTop: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  inp: {
    backgroundColor: '#f8fafc',
    borderWidth: 1.5,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    paddingHorizontal: 13,
    paddingVertical: 11,
    fontSize: 14,
    color: '#0f172a',
    marginBottom: 4,
  },
  row2: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 4,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
    marginTop: 4,
    paddingVertical: 6,
    paddingHorizontal: 2,
  },

  // ── Chips
  chipBtn: {
    borderRadius: 20,
    paddingHorizontal: 13,
    paddingVertical: 6,
    backgroundColor: '#f0f4f8',
    borderWidth: 1.5,
    borderColor: 'transparent',
  },
  chipBtnActive: {
    backgroundColor: P + '15',
    borderColor: P,
  },
  chipTxt: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '600',
  },
  chipTxtActive: {
    color: P,
    fontWeight: '700',
  },

  // ── List Items
  listItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 10,
    padding: 11,
    marginBottom: 6,
    gap: 10,
    borderWidth: 1,
    borderColor: '#eef2f7',
  },

  // ── Section Headers
  secHdr: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eef2f7',
  },
  secHdrTxt: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0f172a',
  },
  secHdrBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: P,
    borderRadius: 8,
    paddingHorizontal: 11,
    paddingVertical: 5,
    gap: 4,
    elevation: 2,
  },
  secHdrBtnTxt: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },

  // ╔══════════════════════════════════════════════════════╗
  // ║  BOTTOM SHEETS                                       ║
  // ╚══════════════════════════════════════════════════════╝
  sheetOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    paddingBottom: Platform.OS === 'ios' ? 38 : 28,
    shadowColor: '#000',
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: -3 },
  },
  sheetHdr: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#eef2f7',
  },
  sheetTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0f172a',
  },
  sortRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: '#f8fafc',
  },
  sortTxt: {
    fontSize: 14,
    color: '#1e293b',
    fontWeight: '500',
  },

});
